import type {
  CastMember,
  Comment,
  Cue,
  Id,
  NoteColumnDef,
  ScriptColumn,
} from "@supermanus/document-model";
import {
  getCueSide,
  isLockAvailable,
  layoutDualColumnSpans,
} from "@supermanus/document-model";
import type { Editor } from "@tiptap/core";
import { TextSelection } from "@tiptap/pm/state";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Superscript from "@tiptap/extension-superscript";
import Subscript from "@tiptap/extension-subscript";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  cueShowsRowRule,
  isAudioOnlyColumnOrder,
  type RowRuleMode,
  type ScriptColumnOrder,
} from "./editorSettings";
import { CellCommentMenu } from "./CellCommentMenu";
import { reorderDropClass } from "./useSceneReorder";
import {
  CastAssignPicker,
  castShortcutMembers,
} from "./CastAssignPicker";
import { isEmptyForAudioOnlyPrint } from "./manuscriptExport";
import { SHOW_REPLIK_UI } from "./replikFeature";
import { SHOW_ROW_SPAN_UI } from "./rowSpanFeature";
import {
  CameraNoteLine,
  CameraNoteSpacer,
  PositionNoteLine,
  PositionNoteSpacer,
  type CueSetupMetaPatch,
} from "./CameraNoteLine";
import {
  CommentDecorations,
  setCommentDecorationOptions,
} from "./commentDecorations";
import { selectionToPlainOffsets } from "./commentOffsets";
import {
  contentForEditor,
  cueContentPlainText,
  isCueContentEmpty as isStoredCueContentEmpty,
  stripTrailingEmptyParagraphs,
} from "./cueRichText";
import { selectQueryOccurrence } from "./findSelect";
import {
  shouldArmNewRowAfterEnter,
  shouldCreateRowOnEnter,
} from "./cueEnter";
import {
  buildCueDeleteConfirmMessage,
  formatCueLockWarningBlock,
  formatCuesLockWarningBlock,
  formatLinkedWarningsBlock,
  listCueLinkedWarnings,
  listCuesLinkedWarningsSummary,
} from "./deleteWarnings";
import { ensureVerticalBandInView, scrollEditorSelectionIntoViewSoon } from "./scrollIntoView";
import {
  SpanConflictDialog,
  type SpanConflictSide,
} from "./VisualSpanConflictDialog";

/**
 * ProseMirror maps some click-induced DOM inserts (empty space in a
 * stretched cell, gutter edge) to a synthetic Enter. After one Return the
 * caret sits in a trailing empty paragraph, so that fake Enter would create
 * a row. Ignore Enter that arrives right after a pointer event.
 */
const POINTER_ENTER_GUARD_MS = 500;
let lastScriptPointerAt = 0;

function noteScriptPointer() {
  lastScriptPointerAt = Date.now();
}

function scriptPointerIsRecent(): boolean {
  return Date.now() - lastScriptPointerAt < POINTER_ENTER_GUARD_MS;
}

function editorPlainText(editor: Editor): string {
  return editor.getText({ blockSeparator: "\n" });
}

function editorHtml(editor: Editor): string {
  return editor.getHTML();
}

/** Strip trailing empty paragraphs created by the first Return. */
function flushContent(editor: Editor): string {
  return stripTrailingEmptyParagraphs(editorHtml(editor));
}

function isCellEmpty(editor: Editor): boolean {
  return editorPlainText(editor).trim() === "";
}

function isModifierOnlyKey(key: string): boolean {
  return (
    key === "Shift" ||
    key === "Meta" ||
    key === "Alt" ||
    key === "Control" ||
    key === "CapsLock"
  );
}

/** Delete trailing empty paragraphs in place so the caret stays at the end. */
function dropTrailingEmptyParagraphsInEditor(editor: Editor): void {
  const { doc } = editor.state;
  let deleteFrom = doc.content.size;
  for (let i = doc.childCount - 1; i >= 1; i--) {
    const child = doc.child(i);
    if (
      child.type.name !== "paragraph" ||
      child.textContent.trim() !== ""
    ) {
      break;
    }
    deleteFrom -= child.nodeSize;
  }
  if (deleteFrom === doc.content.size) return;
  const tr = editor.state.tr.delete(deleteFrom, doc.content.size);
  tr.setSelection(TextSelection.atEnd(tr.doc));
  tr.setMeta("preventUpdate", true);
  editor.view.dispatch(tr);
}

function isCaretAtStart(editor: Editor): boolean {
  if (!editor.state.selection.empty) return false;
  if (isCellEmpty(editor)) return true;
  return editor.state.selection.from <= 1;
}

function isCaretAtEnd(editor: Editor): boolean {
  if (!editor.state.selection.empty) return false;
  if (isCellEmpty(editor)) return true;
  return editor.state.selection.from >= editor.state.doc.content.size - 1;
}

type NavDir = "up" | "down" | "left" | "right";

export type CellRef = {
  cueId: Id;
  column: ScriptColumn;
  /** After focus, select this query occurrence in the cell. */
  selectQuery?: string;
  /** 0-based occurrence within the cell. */
  selectOccurrence?: number;
  /** Bump to re-apply selection on the same cell. */
  selectNonce?: number;
};

type CellKey = `${string}:${ScriptColumn}`;

function cellKey(cueId: Id, column: ScriptColumn): CellKey {
  return `${cueId}:${column}`;
}

/** Layout height of an in-flow sibling (meta row, static badge, …). */
function inFlowOuterHeight(el: HTMLElement): number {
  const cs = getComputedStyle(el);
  if (cs.display === "none") return 0;
  if (cs.position === "absolute" || cs.position === "fixed") return 0;
  const mt = Number.parseFloat(cs.marginTop) || 0;
  const mb = Number.parseFloat(cs.marginBottom) || 0;
  return el.getBoundingClientRect().height + mt + mb;
}

/**
 * Space left inside a cue <td> for .sm-cue-cell after padding and in-flow
 * siblings (setup numbers, camera/position notes). Copying the full td height
 * onto the editor cell grows the row forever once those siblings exist.
 */
function remainingCellFillPx(
  td: HTMLTableCellElement,
  cell: HTMLElement,
): number {
  const cs = getComputedStyle(td);
  const pad =
    (Number.parseFloat(cs.paddingTop) || 0) +
    (Number.parseFloat(cs.paddingBottom) || 0);
  let occupied = 0;
  for (const child of td.children) {
    if (!(child instanceof HTMLElement) || child === cell) continue;
    occupied += inFlowOuterHeight(child);
  }
  return Math.floor(td.clientHeight - pad - occupied);
}

const CELL_LAYOUT_CHROME =
  ".sm-row-add, .sm-row-grip, .sm-row-delete, .sm-cast-assign, .sm-ctx-menu, .sm-comment-menu, .sm-format-toolbar, .sm-cue-comment-badge, .sm-cue-lock, .sm-visual-resize, .sm-audio-resize";

function isCellLayoutChromeNode(node: Node): boolean {
  const el = node instanceof Element ? node : node.parentElement;
  return Boolean(el?.closest(CELL_LAYOUT_CHROME));
}

/** Selection chrome (+ button, menus) must not remeasure row fill. */
function mutationsAffectCellLayout(mutations: MutationRecord[]): boolean {
  for (const mutation of mutations) {
    if (mutation.type === "attributes") return true;
    if (mutation.type === "characterData") {
      if (!isCellLayoutChromeNode(mutation.target)) return true;
      continue;
    }
    for (const node of mutation.addedNodes) {
      if (!isCellLayoutChromeNode(node)) return true;
    }
    for (const node of mutation.removedNodes) {
      if (!isCellLayoutChromeNode(node)) return true;
    }
  }
  return false;
}

function matchCast(
  members: CastMember[],
  characterName: string | undefined | null,
): CastMember | undefined {
  const needle = characterName?.trim().toLowerCase();
  if (!needle) return undefined;
  return members.find((m) => m.name.trim().toLowerCase() === needle);
}

interface SideEditorProps {
  cue: Cue;
  column: ScriptColumn;
  userId: Id;
  readOnly?: boolean;
  canDeletePair?: boolean;
  castMembers?: CastMember[];
  comments?: Comment[];
  showComments?: boolean;
  requestFocus?: boolean;
  /** Select this query in the cell when requestFocus is applied. */
  focusSelectQuery?: string;
  focusSelectOccurrence?: number;
  focusSelectNonce?: number;
  /** Another cell is the pending focus target — drop this cell's active chrome. */
  hideActiveChrome?: boolean;
  onFocused?: () => void;
  onFocus: (cueId: Id, column: ScriptColumn) => void;
  onBlur: (cueId: Id, column: ScriptColumn) => void;
  onChange: (cueId: Id, column: ScriptColumn, content: string) => void;
  onNewPair: (cueId: Id, column: ScriptColumn, flushedContent: string) => void;
  onDeletePair: (cueId: Id) => void;
  onAddComment: (
    cueId: Id,
    column: ScriptColumn,
    body: string,
    range?: { from: number; to: number; quote: string },
  ) => void;
  onResolveComment: (commentId: Id) => void;
  onRemoveComment: (commentId: Id) => void;
  onSetCharacter: (
    cueId: Id,
    column: ScriptColumn,
    characterName: string | null,
  ) => void;
  onNavigate: (from: CellRef, dir: NavDir) => void;
  getLiveContent: (cueId: Id, column: ScriptColumn) => string;
  registerEditor: (
    cueId: Id,
    column: ScriptColumn,
    editor: Editor | null,
  ) => void;
  /** Part of an app-level multi-cell selection. */
  selected?: boolean;
  /** When multi-cell selection is active, Delete/Backspace should clear it. */
  onDeleteSelection?: () => boolean;
  /** Enter on an empty paragraph inserts a new row. Default true. */
  newRowOnDoubleReturn?: boolean;
  /**
   * First mouse click selects all cell text (format applies to the whole cell).
   * Second click in the same cell places the caret. Keyboard focus still edits.
   * Shift-click always selects the cell when `shiftClickSelectsCell` is true.
   */
  clickSelectsCell?: boolean;
  /** Shift-click selects the whole cell. Off while multi-cell select mode is on. */
  shiftClickSelectsCell?: boolean;
  onInsertRow?: () => void;
  onInsertRowBefore?: () => void;
  /** Hang the + button outside the left or right of this cell. */
  insertRowSide?: "left" | "right";
  /** Bump after undo/redo so TipTap pulls document content without remounting. */
  contentResetKey?: number | string;
}

function SideEditor({
  cue,
  column,
  userId,
  readOnly,
  canDeletePair = true,
  castMembers = [],
  comments = [],
  showComments = true,
  requestFocus,
  focusSelectQuery,
  focusSelectOccurrence = 0,
  focusSelectNonce = 0,
  hideActiveChrome = false,
  onFocused,
  onFocus,
  onBlur,
  onChange,
  onNewPair,
  onDeletePair,
  onAddComment,
  onResolveComment,
  onRemoveComment,
  onSetCharacter,
  onNavigate,
  getLiveContent,
  registerEditor,
  selected = false,
  onDeleteSelection,
  newRowOnDoubleReturn = true,
  clickSelectsCell = false,
  shiftClickSelectsCell = true,
  onInsertRow,
  onInsertRowBefore,
  insertRowSide = "left",
  contentResetKey = 0,
}: SideEditorProps) {
  const side = getCueSide(cue, column);
  const cast = matchCast(castMembers, side.characterName);
  const showCharacterName =
    SHOW_REPLIK_UI &&
    Boolean(side.characterName?.trim()) &&
    (cast ? cast.showNameAboveLines : true);
  const dialogueColor = SHOW_REPLIK_UI
    ? cast?.color?.trim() || undefined
    : undefined;
  const lockedByOther = !isLockAvailable(side, userId);
  const editable = !readOnly && !lockedByOther;
  const focusedRef = useRef(false);
  const [isFocused, setIsFocused] = useState(false);
  const cueRef = useRef(cue);
  cueRef.current = cue;
  const canDeleteRef = useRef(canDeletePair);
  canDeleteRef.current = canDeletePair;
  const getLiveContentRef = useRef(getLiveContent);
  getLiveContentRef.current = getLiveContent;
  const castMembersRef = useRef(castMembers);
  castMembersRef.current = castMembers;
  const onSetCharacterRef = useRef(onSetCharacter);
  onSetCharacterRef.current = onSetCharacter;

  const onChangeRef = useRef(onChange);
  const onNewPairRef = useRef(onNewPair);
  const onDeletePairRef = useRef(onDeletePair);
  const onDeleteSelectionRef = useRef(onDeleteSelection);
  const onNavigateRef = useRef(onNavigate);
  const onFocusRef = useRef(onFocus);
  const onBlurRef = useRef(onBlur);
  onChangeRef.current = onChange;
  onNewPairRef.current = onNewPair;
  onDeletePairRef.current = onDeletePair;
  onDeleteSelectionRef.current = onDeleteSelection;
  onNavigateRef.current = onNavigate;
  onFocusRef.current = onFocus;
  onBlurRef.current = onBlur;

  const editorRef = useRef<Editor | null>(null);
  const suppressScrollRef = useRef(false);
  const lastContentResetKeyRef = useRef(contentResetKey);
  const newRowArmedRef = useRef(false);
  const pointerModeRef = useRef<"idle" | "selected" | "editing">("idle");
  const [contentSelected, setContentSelected] = useState(false);
  const clickSelectsCellRef = useRef(clickSelectsCell);
  clickSelectsCellRef.current = clickSelectsCell;
  const shiftClickSelectsCellRef = useRef(shiftClickSelectsCell);
  shiftClickSelectsCellRef.current = shiftClickSelectsCell;

  function setWholeCellPick(on: boolean) {
    setContentSelected(on);
    const cell = editorRef.current?.view.dom.closest(".sm-cue-cell");
    if (cell instanceof HTMLElement) {
      cell.classList.toggle("is-content-selected", on);
    }
  }

  const handlePointerSelectRef = useRef<
    (event: {
      button: number;
      shiftKey?: boolean;
      preventDefault: () => void;
    }) => boolean
  >(() => false);
  handlePointerSelectRef.current = (event) => {
    if (!editable) return false;
    if (event.button !== 0) return false;

    const pickWholeCell = () => {
      const ed = editorRef.current;
      if (!ed) return;
      pointerModeRef.current = "selected";
      setWholeCellPick(true);
      ed.chain()
        .focus(undefined, { scrollIntoView: false })
        .selectAll()
        .run();
      window.setTimeout(() => {
        if (
          pointerModeRef.current === "selected" &&
          editorRef.current &&
          !editorRef.current.isDestroyed
        ) {
          editorRef.current.commands.selectAll();
        }
      }, 0);
    };

    if (event.shiftKey) {
      if (!shiftClickSelectsCellRef.current) return false;
      event.preventDefault();
      pickWholeCell();
      return true;
    }
    if (pointerModeRef.current === "selected") {
      pointerModeRef.current = "editing";
      setWholeCellPick(false);
      return false;
    }
    if (!clickSelectsCellRef.current) return false;
    if (pointerModeRef.current === "idle") {
      event.preventDefault();
      pickWholeCell();
      return true;
    }
    return false;
  };
  const pendingRangeRef = useRef<{
    from: number;
    to: number;
    quote: string;
  } | null>(null);
  const [menu, setMenu] = useState<{
    x: number;
    y: number;
    mode: "create-cell" | "create-range" | "list";
    quote?: string;
  } | null>(null);

  const tryDeleteEmptyPair = useCallback((): boolean => {
    if (!canDeleteRef.current) return false;
    const live = cueRef.current;
    const otherColumn: ScriptColumn =
      column === "visual" ? "audio" : "visual";
    const other = getLiveContentRef.current(cue.id, otherColumn);
    if (
      !isStoredCueContentEmpty(other) ||
      live.audio.characterName?.trim() ||
      live.visual.characterName?.trim()
    ) {
      return false;
    }
    onDeletePairRef.current(cue.id);
    return true;
  }, [column, cue.id]);

  const assignCastByIndex = useCallback(
    (index: number): boolean => {
      if (!editable) return false;
      const list = castShortcutMembers(castMembersRef.current);
      if (index === 0) {
        onSetCharacterRef.current(cue.id, column, null);
        return true;
      }
      const member = list[index - 1];
      if (!member) return false;
      onSetCharacterRef.current(cue.id, column, member.name);
      return true;
    },
    [column, cue.id, editable],
  );

  const handleKeyDownRef = useRef<(event: KeyboardEvent) => boolean>(
    () => false,
  );
  handleKeyDownRef.current = (event: KeyboardEvent) => {
    const editor = editorRef.current;
    if (!editor || !editable) return false;

    if (
      SHOW_REPLIK_UI &&
      (event.metaKey || event.ctrlKey) &&
      !event.altKey &&
      !event.shiftKey
    ) {
      const digit = event.key;
      if (digit >= "0" && digit <= "9") {
        if (assignCastByIndex(Number(digit))) {
          event.preventDefault();
          return true;
        }
      }
    }

    if (event.key !== "Enter" && !isModifierOnlyKey(event.key)) {
      newRowArmedRef.current = false;
    }

    if (
      pointerModeRef.current === "selected" &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey
    ) {
      if (event.key !== "Tab") {
        if (
          event.key === "ArrowUp" ||
          event.key === "ArrowDown" ||
          event.key === "ArrowLeft" ||
          event.key === "ArrowRight"
        ) {
          if (event.shiftKey) return false;
          event.preventDefault();
          const dir =
            event.key === "ArrowUp"
              ? "up"
              : event.key === "ArrowDown"
                ? "down"
                : event.key === "ArrowLeft"
                  ? "left"
                  : "right";
          onNavigateRef.current({ cueId: cue.id, column }, dir);
          return true;
        }
        if (event.key === "Backspace" || event.key === "Delete") {
          if (onDeleteSelectionRef.current?.()) {
            event.preventDefault();
            return true;
          }
          if (isCellEmpty(editor)) return tryDeleteEmptyPair();
          event.preventDefault();
          return true;
        }
        if (event.key === "Enter") {
          event.preventDefault();
          newRowArmedRef.current = false;
          return true;
        }
        if (!isModifierOnlyKey(event.key)) {
          if (isCellEmpty(editor)) {
            pointerModeRef.current = "editing";
            setWholeCellPick(false);
            editor.commands.focus("end", { scrollIntoView: false });
            return false;
          }
          event.preventDefault();
          return true;
        }
      }
    }

    if (event.key === "Backspace" || event.key === "Delete") {
      if (onDeleteSelectionRef.current?.()) {
        event.preventDefault();
        return true;
      }
      if (!isCellEmpty(editor)) return false;
      return tryDeleteEmptyPair();
    }

    if (event.key === "Enter") {
      if (event.metaKey || event.ctrlKey || event.altKey) {
        newRowArmedRef.current = false;
        return false;
      }
      if (event.shiftKey) {
        newRowArmedRef.current = false;
        editor.commands.splitBlock();
        return true;
      }
      if (scriptPointerIsRecent()) {
        newRowArmedRef.current = false;
        return false;
      }
      if (
        newRowOnDoubleReturn &&
        shouldCreateRowOnEnter(editor, newRowArmedRef.current)
      ) {
        event.preventDefault();
        newRowArmedRef.current = false;
        dropTrailingEmptyParagraphsInEditor(editor);
        const flushed = flushContent(editor);
        onNewPairRef.current(cue.id, column, flushed);
        return true;
      }
      newRowArmedRef.current = shouldArmNewRowAfterEnter(editor);
      return false;
    }

    /* Excel/Numbers-style: Tab / Shift+Tab always move to next/prev cell. */
    if (event.key === "Tab") {
      event.preventDefault();
      onNavigateRef.current(
        { cueId: cue.id, column },
        event.shiftKey ? "left" : "right",
      );
      return true;
    }

    if (event.key === "ArrowUp") {
      if (!editor.state.selection.empty) return false;
      if (isCellEmpty(editor)) {
        onNavigateRef.current({ cueId: cue.id, column }, "up");
        return true;
      }
      const view = editor.view;
      const from = editor.state.selection.from;
      const start = view.coordsAtPos(1);
      const here = view.coordsAtPos(from);
      if (here.top <= start.top + 4) {
        onNavigateRef.current({ cueId: cue.id, column }, "up");
        return true;
      }
      return false;
    }

    if (event.key === "ArrowDown") {
      if (!editor.state.selection.empty) return false;
      if (isCellEmpty(editor)) {
        onNavigateRef.current({ cueId: cue.id, column }, "down");
        return true;
      }
      const view = editor.view;
      const from = editor.state.selection.from;
      const endPos = editor.state.doc.content.size;
      const end = view.coordsAtPos(endPos);
      const here = view.coordsAtPos(from);
      if (here.bottom >= end.bottom - 4) {
        onNavigateRef.current({ cueId: cue.id, column }, "down");
        return true;
      }
      return false;
    }

    if (event.key === "ArrowLeft") {
      if (!isCaretAtStart(editor)) return false;
      onNavigateRef.current({ cueId: cue.id, column }, "left");
      return true;
    }

    if (event.key === "ArrowRight") {
      if (!isCaretAtEnd(editor)) return false;
      onNavigateRef.current({ cueId: cue.id, column }, "right");
      return true;
    }

    return false;
  };

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: false,
          codeBlock: false,
          blockquote: false,
          bulletList: false,
          orderedList: false,
          listItem: false,
          listKeymap: false,
          code: false,
          horizontalRule: false,
          undoRedo: false,
          trailingNode: false,
        }),
        Underline,
        Superscript,
        Subscript,
        TextStyle,
        Color,
        CommentDecorations.configure({
          ranges: [],
          visible: showComments,
        }),
      ],
      content: contentForEditor(side.content),
      editable,
      immediatelyRender: false,
      editorProps: {
        attributes: {
          spellcheck: "false",
        },
        handleKeyDown: (_view, event) => handleKeyDownRef.current(event),
        handleClick: () => {
          noteScriptPointer();
          newRowArmedRef.current = false;
          if (pointerModeRef.current === "selected") return true;
          return false;
        },
        handlePaste: () => {
          if (pointerModeRef.current !== "selected") return false;
          const ed = editorRef.current;
          if (ed && isCellEmpty(ed)) {
            pointerModeRef.current = "editing";
            setWholeCellPick(false);
            return false;
          }
          return true;
        },
        handleDrop: () => pointerModeRef.current === "selected",
        handleScrollToSelection: (view) => {
          if (suppressScrollRef.current) return true;
          try {
            const head = view.state.selection.head;
            const coords = view.coordsAtPos(head);
            ensureVerticalBandInView(
              coords.top,
              coords.bottom,
              view.dom as HTMLElement,
            );
          } catch {
            /* ignore invalid positions during teardown */
          }
          return true;
        },
        handleDOMEvents: {
          mousedown: (_view, event) => {
            noteScriptPointer();
            newRowArmedRef.current = false;
            return handlePointerSelectRef.current(event);
          },
          mouseup: () => pointerModeRef.current === "selected",
          contextmenu: (_view, event) => {
            event.preventDefault();
            const ed = editorRef.current;
            pendingRangeRef.current = null;
            let mode: "create-cell" | "create-range" | "list" = readOnly
              ? "list"
              : "create-cell";
            let quote: string | undefined;
            if (ed && !ed.state.selection.empty) {
              const range = selectionToPlainOffsets(
                ed.state.doc,
                ed.state.selection.from,
                ed.state.selection.to,
              );
              if (range) {
                pendingRangeRef.current = range;
                mode = readOnly ? "list" : "create-range";
                quote = range.quote;
              }
            } else if (readOnly && comments.length === 0) {
              return true;
            }
            setMenu({
              x: event.clientX,
              y: event.clientY,
              mode,
              quote,
            });
            return true;
          },
        },
      },
      onUpdate: ({ editor: ed }) => {
        if (!focusedRef.current) return;
        onChangeRef.current(cue.id, column, flushContent(ed));
      },
      onFocus: () => {
        focusedRef.current = true;
        setIsFocused(true);
        onFocusRef.current(cue.id, column);
      },
      onBlur: () => {
        window.setTimeout(() => {
          const active = document.activeElement;
          if (
            active?.closest(".sm-format-toolbar") ||
            active?.closest(".sm-format-color-palette") ||
            active?.closest(".sm-row-add")
          ) {
            return;
          }
          // Comment/context menus live inside the cell and steal focus; if we
          // treat that as "still focused", .is-focused sticks after the menu
          // closes (editor already blurred, so no second blur fires).
          const inCommentUi = !!(
            active?.closest(".sm-comment-menu") ||
            active?.closest(".sm-ctx-menu")
          );
          const cell = editorRef.current?.view.dom.closest(".sm-cue-cell");
          if (
            !inCommentUi &&
            cell instanceof HTMLElement &&
            active &&
            cell.contains(active)
          ) {
            return;
          }
          const ed = editorRef.current;
          // Flush live HTML before clearing focus — otherwise the last
          // keystrokes can sit only in TipTap and be wiped by setContent.
          if (ed && !ed.isDestroyed && focusedRef.current) {
            onChangeRef.current(cue.id, column, flushContent(ed));
          }
          newRowArmedRef.current = false;
          pointerModeRef.current = "idle";
          setWholeCellPick(false);
          focusedRef.current = false;
          setIsFocused(false);
          onBlurRef.current(cue.id, column);
        }, 0);
      },
    },
    [cue.id, column],
  );

  editorRef.current = editor;

  useEffect(() => {
    if (!editor) return;
    setCommentDecorationOptions(editor, {
      ranges: comments
        .filter((c) => c.from !== undefined && c.to !== undefined)
        .map((c) => ({
          id: c.id,
          from: c.from,
          to: c.to,
          quote: c.quote,
        })),
      visible: showComments,
    });
  }, [editor, comments, showComments]);

  useEffect(() => {
    registerEditor(cue.id, column, editor);
    return () => registerEditor(cue.id, column, null);
  }, [editor, cue.id, column, registerEditor]);

  // Pull parent content when unfocused. After undo/redo, also replace
  // focused editors so they match the restored document — without remounting.
  useLayoutEffect(() => {
    if (!editor) return;
    const historyBump = lastContentResetKeyRef.current !== contentResetKey;
    lastContentResetKeyRef.current = contentResetKey;
    if (focusedRef.current && !historyBump) return;
    const current = editorHtml(editor);
    if (current === side.content) return;
    suppressScrollRef.current = true;
    try {
      editor.commands.setContent(contentForEditor(side.content), {
        emitUpdate: false,
      });
    } finally {
      suppressScrollRef.current = false;
    }
  }, [side.content, editor, contentResetKey]);

  useEffect(() => {
    editor?.setEditable(editable);
  }, [editable, editor]);

  useEffect(() => {
    if (!editor) return;
    const keepWholeCell = () => {
      if (pointerModeRef.current !== "selected") return;
      const { selection, doc } = editor.state;
      if (selection.from <= 1 && selection.to >= doc.content.size - 1) return;
      editor.commands.selectAll();
    };
    editor.on("selectionUpdate", keepWholeCell);
    return () => {
      editor.off("selectionUpdate", keepWholeCell);
    };
  }, [editor]);

  useLayoutEffect(() => {
    if (!requestFocus || !editor) return;
    pointerModeRef.current = "editing";
    setWholeCellPick(false);
    const selected =
      focusSelectQuery &&
      selectQueryOccurrence(editor, focusSelectQuery, focusSelectOccurrence);
    if (!selected) {
      editor.commands.focus("end", { scrollIntoView: false });
    }
    scrollEditorSelectionIntoViewSoon(editor);
  }, [
    requestFocus,
    editor,
    focusSelectQuery,
    focusSelectOccurrence,
    focusSelectNonce,
  ]);

  useEffect(() => {
    if (!requestFocus || !editor) return;
    pointerModeRef.current = "editing";
    setWholeCellPick(false);
    const tryFocus = () => {
      if (editor.isDestroyed) return false;
      if (focusSelectQuery) {
        selectQueryOccurrence(
          editor,
          focusSelectQuery,
          focusSelectOccurrence,
        );
      } else if (!editor.isFocused) {
        editor.commands.focus("end", { scrollIntoView: false });
      }
      return editor.isFocused;
    };
    tryFocus();
    const id = window.setTimeout(() => {
      if (tryFocus()) {
        scrollEditorSelectionIntoViewSoon(editor);
        onFocused?.();
      }
    }, 0);
    return () => window.clearTimeout(id);
  }, [
    requestFocus,
    editor,
    onFocused,
    focusSelectQuery,
    focusSelectOccurrence,
    focusSelectNonce,
  ]);

  const showActiveChrome = requestFocus || (isFocused && !hideActiveChrome);

  return (
    <div
      className={`sm-cue-cell sm-cue-${column}${lockedByOther ? " locked-other" : ""}${isStoredCueContentEmpty(side.content) ? " is-empty" : ""}${showComments && comments.length > 0 ? " has-comments" : ""}${showActiveChrome ? " is-focused" : ""}${selected ? " is-cell-selected" : ""}${contentSelected ? " is-content-selected" : ""}`}
      data-cue-id={cue.id}
      data-column={column}
      style={dialogueColor ? { color: dialogueColor } : undefined}
      onMouseDown={(e) => {
        noteScriptPointer();
        if (!editable || !editor) return;
        const target = e.target as HTMLElement;
        if (
          target.closest(
            "button, input, textarea, select, label, .sm-cast-assign, .sm-camera-note, .sm-ctx-menu, .sm-comment-menu, .sm-format-toolbar, .sm-row-add",
          )
        ) {
          return;
        }
        if (target.closest(".ProseMirror")) return;
        if (handlePointerSelectRef.current(e)) return;
        e.preventDefault();
        const pos = editor.view.posAtCoords({
          left: e.clientX,
          top: e.clientY,
        });
        if (pos) {
          editor.chain().focus(undefined, { scrollIntoView: false }).setTextSelection(pos.pos).run();
        } else {
          editor.commands.focus("end", { scrollIntoView: false });
        }
      }}
    >
      {editable && onInsertRowBefore ? (
        <button
          type="button"
          className="sm-row-add sm-row-add--above sm-no-print"
          title="Tilføj række ovenover"
          aria-label="Tilføj række ovenover"
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onInsertRowBefore()}
        >
          +
        </button>
      ) : null}
      {editable && onInsertRow ? (
        <button
          type="button"
          className={`sm-row-add sm-row-add--${insertRowSide} sm-no-print`}
          title="Tilføj række nedenunder"
          aria-label="Tilføj række nedenunder"
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onInsertRow()}
        >
          +
        </button>
      ) : null}
      {SHOW_REPLIK_UI &&
      showActiveChrome &&
      editable &&
      castMembers.length > 0 ? (
        <CastAssignPicker
          members={castMembers}
          value={side.characterName}
          onSelect={(name) => onSetCharacter(cue.id, column, name)}
          onClear={() => onSetCharacter(cue.id, column, null)}
        />
      ) : showCharacterName ? (
        <button
          type="button"
          className="sm-cue-character"
          disabled={readOnly || lockedByOther}
          title="Klik for at skifte medvirkende"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            if (readOnly || lockedByOther) return;
            editor?.commands.focus(undefined, { scrollIntoView: false });
          }}
        >
          {side.characterName}
        </button>
      ) : null}
      {showComments && comments.length > 0 ? (
        <button
          type="button"
          className="sm-cue-comment-badge"
          title={`${comments.length} kommentar${comments.length === 1 ? "" : "er"}`}
          onClick={(e) => {
            e.preventDefault();
            setMenu({
              x: e.clientX,
              y: e.clientY,
              mode: "list",
            });
          }}
        >
          {comments.length}
        </button>
      ) : null}
      {lockedByOther && side.lockOwnerName ? (
        <span
          className="sm-cue-lock"
          title={`Låst af ${side.lockOwnerName}`}
        >
          Låst af <strong>{side.lockOwnerName}</strong>
        </span>
      ) : null}
      <EditorContent editor={editor} />
      {menu ? (
        <CellCommentMenu
          x={menu.x}
          y={menu.y}
          mode={menu.mode}
          column={column}
          selectionQuote={menu.quote}
          comments={comments}
          readOnly={readOnly}
          onAdd={(body) => {
            onAddComment(
              cue.id,
              column,
              body,
              pendingRangeRef.current ?? undefined,
            );
            pendingRangeRef.current = null;
          }}
          onResolve={onResolveComment}
          onRemove={onRemoveComment}
          onClose={() => {
            setMenu(null);
            // Safety: menu may close without a fresh editor blur.
            window.setTimeout(() => {
              if (!editorRef.current?.isFocused) {
                focusedRef.current = false;
                setIsFocused(false);
              }
            }, 0);
          }}
        />
      ) : null}
    </div>
  );
}

function isCueContentEmpty(
  cue: Cue,
  visualContent: string,
  audioContent: string,
): boolean {
  return (
    isStoredCueContentEmpty(visualContent) &&
    isStoredCueContentEmpty(audioContent) &&
    !cue.visual.characterName?.trim() &&
    !cue.audio.characterName?.trim()
  );
}

function formatCueDeletePreview(
  cue: Cue,
  visualContent: string,
  audioContent: string,
): string {
  const parts: string[] = [];
  const visualName = SHOW_REPLIK_UI
    ? cue.visual.characterName?.trim()
    : undefined;
  const audioName = SHOW_REPLIK_UI
    ? cue.audio.characterName?.trim()
    : undefined;
  const visualBody = cueContentPlainText(visualContent).trim();
  const audioBody = cueContentPlainText(audioContent).trim();

  const visualBlock = [visualName, visualBody].filter(Boolean).join("\n");
  const audioBlock = [audioName, audioBody].filter(Boolean).join("\n");

  if (visualBlock) parts.push(`Billedside:\n${visualBlock}`);
  if (audioBlock) parts.push(`Lydside:\n${audioBlock}`);

  let preview = parts.join("\n\n") || "(tom række)";
  const max = 900;
  if (preview.length > max) {
    preview = `${preview.slice(0, max).trimEnd()}…`;
  }
  return preview;
}

/** Cue ids in scene order for a cell selection (unique rows). */
function cueIdsInSelection(
  cues: Cue[],
  rows: LayoutRow[],
  selection: CellSelectionRange,
): Id[] {
  const selected = new Set(
    cellsInRange(rows, selection.anchor, selection.focus).map((c) => c.cueId),
  );
  return cues.map((c) => c.id).filter((id) => selected.has(id));
}

type LayoutRow = {
  cue: Cue;
  index: number;
  /** Rowspan for visual cell; 0 = covered by an earlier visual span. */
  visualSpan: number;
  /** Rowspan for audio cell; 0 = covered by an earlier audio span. */
  audioSpan: number;
  /** Cue id that owns the reorder block containing this row. */
  blockCueId: Id;
};

function layoutCues(
  cues: Cue[],
  opts?: { audioOnly?: boolean; keepCueIds?: ReadonlySet<Id> },
): LayoutRow[] {
  if (opts?.audioOnly) {
    const rows: LayoutRow[] = [];
    for (let i = 0; i < cues.length; i++) {
      const cue = cues[i]!;
      if (
        isEmptyForAudioOnlyPrint(cue) &&
        !opts.keepCueIds?.has(cue.id)
      ) {
        continue;
      }
      rows.push({
        cue,
        index: i,
        visualSpan: 0,
        audioSpan: 1,
        blockCueId: cue.id,
      });
    }
    return rows;
  }

  const spans = layoutDualColumnSpans(cues);
  const rows: LayoutRow[] = [];
  let blockUntil = -1;
  let blockCueId: Id | null = null;

  for (let i = 0; i < cues.length; i++) {
    const cue = cues[i]!;
    const visualSpan = spans[i]?.visualSpan ?? 1;
    const audioSpan = spans[i]?.audioSpan ?? 1;

    if (i > blockUntil) {
      const size = Math.max(
        visualSpan > 0 ? visualSpan : 0,
        audioSpan > 0 ? audioSpan : 0,
        1,
      );
      blockCueId = cue.id;
      blockUntil = i + Math.min(size, cues.length - i) - 1;
    }

    rows.push({
      cue,
      index: i,
      visualSpan,
      audioSpan,
      blockCueId: blockCueId ?? cue.id,
    });
  }
  return rows;
}

function coveredSidePreviews(
  cues: Cue[],
  ownerIndex: number,
  prevSpan: number,
  nextSpan: number,
  column: ScriptColumn,
): string[] {
  if (nextSpan <= prevSpan) return [];
  const out: string[] = [];
  for (
    let i = ownerIndex + prevSpan;
    i < ownerIndex + nextSpan && i < cues.length;
    i++
  ) {
    const text = cueContentPlainText(
      (column === "visual" ? cues[i]!.visual : cues[i]!.audio).content,
    ).trim();
    if (!text) continue;
    out.push(text.length > 120 ? `${text.slice(0, 120)}…` : text);
  }
  return out;
}

type SpanGhostRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

/** Live dashed outline for the span that would apply if the user released now. */
function measureSpanGhost(
  table: HTMLTableElement,
  ownerIndex: number,
  previewSpan: number,
  side: SpanConflictSide,
): SpanGhostRect | null {
  const shell = table.closest(".sm-dual-table-shell") as HTMLElement | null;
  const originEl = shell ?? table;
  const origin = originEl.getBoundingClientRect();

  const ownerRow = table.querySelector<HTMLElement>(
    `tr.sm-cue-row[data-cue-index="${ownerIndex}"]`,
  );
  const endIndex = ownerIndex + Math.max(1, previewSpan) - 1;
  const endRow = table.querySelector<HTMLElement>(
    `tr.sm-cue-row[data-cue-index="${endIndex}"]`,
  );
  if (!ownerRow || !endRow) return null;

  const ownerRect = ownerRow.getBoundingClientRect();
  const endRect = endRow.getBoundingClientRect();
  const cellSel =
    side === "visual" ? "td.sm-cue-visual-cell" : "td.sm-cue-audio-cell";
  const cell =
    ownerRow.querySelector<HTMLElement>(cellSel) ??
    table.querySelector<HTMLElement>(`tbody ${cellSel}`);
  if (!cell) {
    const th = table.querySelector<HTMLElement>(
      `thead th[data-column="${side}"]`,
    );
    if (!th) return null;
    const thRect = th.getBoundingClientRect();
    return {
      top: ownerRect.top - origin.top + originEl.scrollTop,
      left: thRect.left - origin.left + originEl.scrollLeft,
      width: thRect.width,
      height: Math.max(0, endRect.bottom - ownerRect.top),
    };
  }
  const cellRect = cell.getBoundingClientRect();
  return {
    top: ownerRect.top - origin.top + originEl.scrollTop,
    left: cellRect.left - origin.left + originEl.scrollLeft,
    width: cellRect.width,
    height: Math.max(0, endRect.bottom - ownerRect.top),
  };
}

/** Flat navigation order: each rendered cell in left→right, top→bottom order. */
function buildNavOrder(
  cues: Cue[],
  columnOrder: ScriptColumnOrder = "visual-audio",
  keepCueIds?: ReadonlySet<Id>,
): CellRef[] {
  const audioOnly = isAudioOnlyColumnOrder(columnOrder);
  const rows = layoutCues(cues, { audioOnly, keepCueIds });
  const cols: ScriptColumn[] = audioOnly
    ? ["audio"]
    : columnOrder === "audio-visual"
      ? ["audio", "visual"]
      : ["visual", "audio"];
  const order: CellRef[] = [];
  for (const row of rows) {
    for (const column of cols) {
      const span = column === "visual" ? row.visualSpan : row.audioSpan;
      if (span > 0) {
        order.push({ cueId: row.cue.id, column });
      }
    }
  }
  return order;
}

function sameCell(a: CellRef, b: CellRef): boolean {
  return a.cueId === b.cueId && a.column === b.column;
}

function parseCellElement(el: Element | null): CellRef | null {
  const cell = el?.closest?.(".sm-cue-cell") as HTMLElement | null;
  if (!cell) return null;
  const cueId = cell.dataset.cueId;
  const column = cell.dataset.column;
  if (!cueId || (column !== "visual" && column !== "audio")) return null;
  return { cueId, column };
}

function resolveCellFromPoint(clientX: number, clientY: number): CellRef | null {
  const el = document.elementFromPoint(clientX, clientY);
  if (!(el instanceof Element)) return null;
  return parseCellElement(el);
}

function isCellSelectChrome(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest(
      ".sm-row-grip, .sm-row-delete, .sm-row-add, .sm-visual-resize, .sm-audio-resize, .sm-row-rule-hit, .sm-cast-assign, .sm-camera-note, .sm-ctx-menu, .sm-comment-menu, button, input, textarea, select, label",
    ),
  );
}

/** Layout row index where this cell is the span owner (−1 if not found). */
function rowIndexForCell(rows: LayoutRow[], cell: CellRef): number {
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]!;
    if (r.cue.id !== cell.cueId) continue;
    if (cell.column === "visual" && r.visualSpan > 0) return i;
    if (cell.column === "audio" && r.audioSpan > 0) return i;
  }
  return -1;
}

/**
 * Cells in a rectangular selection.
 * Same column → that column only between anchor/focus rows.
 * Different columns → both columns for all rows in the vertical span.
 */
function cellsInRange(
  rows: LayoutRow[],
  anchor: CellRef,
  focus: CellRef,
): CellRef[] {
  const aIdx = rowIndexForCell(rows, anchor);
  const fIdx = rowIndexForCell(rows, focus);
  if (aIdx < 0 || fIdx < 0) return [anchor];

  const lo = Math.min(aIdx, fIdx);
  const hi = Math.max(aIdx, fIdx);
  const result: CellRef[] = [];

  if (anchor.column === focus.column) {
    const col = anchor.column;
    for (let i = lo; i <= hi; i++) {
      const r = rows[i]!;
      if (col === "visual" && r.visualSpan > 0) {
        result.push({ cueId: r.cue.id, column: "visual" });
      }
      if (col === "audio" && r.audioSpan > 0) {
        result.push({ cueId: r.cue.id, column: "audio" });
      }
    }
  } else {
    for (let i = lo; i <= hi; i++) {
      const r = rows[i]!;
      if (r.visualSpan > 0) {
        result.push({ cueId: r.cue.id, column: "visual" });
      }
      if (r.audioSpan > 0) {
        result.push({ cueId: r.cue.id, column: "audio" });
      }
    }
  }
  return result;
}

function formatSelectionPlainText(
  selected: CellRef[],
  rows: LayoutRow[],
  getContent: (cueId: Id, column: ScriptColumn) => string,
  columnOrder: ScriptColumnOrder = "visual-audio",
): string {
  if (selected.length === 0) return "";
  const columns = new Set(selected.map((c) => c.column));
  if (columns.size === 1) {
    return selected
      .map((c) => getContent(c.cueId, c.column))
      .join("\n\n");
  }

  const selectedSet = new Set(
    selected.map((c) => cellKey(c.cueId, c.column)),
  );
  const indices = selected
    .map((c) => rowIndexForCell(rows, c))
    .filter((i) => i >= 0);
  if (indices.length === 0) return "";
  const lo = Math.min(...indices);
  const hi = Math.max(...indices);
  const leftFirst = columnOrder === "visual-audio";
  const lines: string[] = [];
  for (let i = lo; i <= hi; i++) {
    const r = rows[i]!;
    const visual =
      r.visualSpan > 0 && selectedSet.has(cellKey(r.cue.id, "visual"))
        ? getContent(r.cue.id, "visual")
        : "";
    const audio =
      r.audioSpan > 0 && selectedSet.has(cellKey(r.cue.id, "audio"))
        ? getContent(r.cue.id, "audio")
        : "";
    if (isAudioOnlyColumnOrder(columnOrder)) {
      lines.push(audio);
    } else {
      lines.push(
        leftFirst ? `${visual}\t${audio}` : `${audio}\t${visual}`,
      );
    }
  }
  return lines.join("\n");
}

type CellSelectionRange = { anchor: CellRef; focus: CellRef };

function findSpanOwnerId(
  cues: Cue[],
  cueId: Id,
  column: ScriptColumn,
): Id | null {
  const rows = layoutCues(cues);
  const target = rows.find((r) => r.cue.id === cueId);
  if (!target) return null;
  if (column === "visual") {
    if (target.visualSpan > 0) return target.cue.id;
    for (let i = target.index - 1; i >= 0; i--) {
      if (rows[i]!.visualSpan > 0) return rows[i]!.cue.id;
    }
  } else {
    if (target.audioSpan > 0) return target.cue.id;
    for (let i = target.index - 1; i >= 0; i--) {
      if (rows[i]!.audioSpan > 0) return rows[i]!.cue.id;
    }
  }
  return null;
}

export interface DualColumnScriptProps {
  cues: Cue[];
  userId: Id;
  readOnly?: boolean;
  confirmDeleteEmptyPair?: boolean;
  focusCell?: CellRef | null;
  onFocusCellHandled?: (cell: CellRef) => void;
  /** Bump after undo/redo so cell editors match the document without remounting. */
  contentResetKey?: number | string;
  onFocus: (cueId: Id, column: ScriptColumn) => void;
  onBlur: (cueId: Id, column: ScriptColumn) => void;
  /** Focused TipTap editor for the format toolbar (null when none). */
  onActiveEditorChange?: (editor: Editor | null) => void;
  onChange: (cueId: Id, column: ScriptColumn, content: string) => void;
  /** Reorder a layout block to sit before `beforeCueId` (null = end). */
  onMoveCueBlock: (cueId: Id, beforeCueId: Id | null) => void;
  onNewPair: (
    afterCueId: Id,
    column: ScriptColumn,
    flushedContent: string,
    place?: "after" | "before",
  ) => Id | null;
  onDeletePair: (cueId: Id) => void;
  /** Delete several cue pairs at once (multi-cell selection). */
  onDeletePairs?: (cueIds: Id[]) => void;
  /** Set how many rows the billedside covers. */
  onSetVisualSpan: (
    cueId: Id,
    span: number,
    coveredVisual?: "clear" | "merge",
  ) => void;
  /** Set how many rows the lydside covers. */
  onSetAudioSpan: (
    cueId: Id,
    span: number,
    coveredAudio?: "clear" | "merge",
  ) => void;
  onTogglePrintBorderBelow: (cueId: Id) => void;
  /**
   * After “Opdel scene”: click the line between two rows. Editors are
   * locked by the parent (`readOnly`); this only shows the split hits.
   */
  splitPickMode?: boolean;
  onPickSplitAt?: (atCueId: Id) => void;
  /** Vertical rule between billedside and lydside (print + editor). Default true. */
  showColumnRule?: boolean;
  /** Horizontal rules: all / none / per-cue custom. Default custom. */
  rowRuleMode?: RowRuleMode;
  /**
   * Custom-rule hover/click only in the gutter between columns.
   * Default true. Ignored when only one column is shown.
   */
  rowRuleHitInGutter?: boolean;
  /** Production cast — used for dialogue color and name-above-lines. */
  castMembers?: CastMember[];
  comments?: Comment[];
  showComments?: boolean;
  onAddComment?: (
    cueId: Id,
    column: ScriptColumn,
    body: string,
    range?: { from: number; to: number; quote: string },
  ) => void;
  onResolveComment?: (commentId: Id) => void;
  onRemoveComment?: (commentId: Id) => void;
  onSetCharacter?: (
    cueId: Id,
    column: ScriptColumn,
    characterName: string | null,
  ) => void;
  /** Scene display number used for setup labels (X in X-Y). */
  sceneNumber?: string | number;
  /** Show X-Y setup numbers in display column 1. */
  showSetupNumbers?: boolean;
  /** Show KAM N + optional camera note in display column 1. */
  showCameraNotes?: boolean;
  /** Show position notes in display column 2. */
  showPosition?: boolean;
  onCameraMetaChange?: (cueId: Id, patch: CueSetupMetaPatch) => void;
  /** Left→right column order. */
  columnOrder?: ScriptColumnOrder;
  /** When true, drag selects a cell range (copy/delete). Off = text selection in the cell. */
  cellSelectMode?: boolean;
  /** When true, the bottom edge of visual/audio cells can be dragged to cover more rows. Hidden unless SHOW_ROW_SPAN_UI. */
  rowSpanMode?: boolean;
  /** Enter on an empty paragraph inserts a new row. Default true. */
  newRowOnDoubleReturn?: boolean;
  /** First mouse click selects the whole cell; second click places the caret. Default off. Shift-click always selects. */
  clickSelectsCell?: boolean;
  /** Production note columns — used when confirming delete of rows with notes. */
  noteColumns?: NoteColumnDef[];
}

export function DualColumnScript({
  cues,
  userId,
  readOnly,
  confirmDeleteEmptyPair = false,
  focusCell,
  onFocusCellHandled,
  contentResetKey = 0,
  onFocus,
  onBlur,
  onActiveEditorChange,
  onChange,
  onMoveCueBlock,
  onNewPair,
  onDeletePair,
  onDeletePairs,
  onSetVisualSpan,
  onSetAudioSpan,
  onTogglePrintBorderBelow,
  splitPickMode = false,
  onPickSplitAt,
  showColumnRule = true,
  rowRuleMode = "custom",
  rowRuleHitInGutter = true,
  castMembers = [],
  comments = [],
  showComments = true,
  onAddComment,
  onResolveComment,
  onRemoveComment,
  onSetCharacter,
  sceneNumber = 1,
  showSetupNumbers = false,
  showCameraNotes = false,
  showPosition = false,
  onCameraMetaChange,
  columnOrder = "visual-audio",
  cellSelectMode = false,
  rowSpanMode = false,
  newRowOnDoubleReturn = true,
  clickSelectsCell = false,
  noteColumns = [],
}: DualColumnScriptProps) {
  const spanEdit = SHOW_ROW_SPAN_UI && rowSpanMode;
  const audioOnly = isAudioOnlyColumnOrder(columnOrder);
  const visualIsLeft = columnOrder === "visual-audio";
  const leftColumn: ScriptColumn = audioOnly || !visualIsLeft ? "audio" : "visual";
  const rightColumn: ScriptColumn | null = audioOnly
    ? null
    : visualIsLeft
      ? "audio"
      : "visual";
  const [pendingFocus, setPendingFocus] = useStateFocus(focusCell);
  /** Keep empty audio-only rows visible while focused (new row / typing). */
  const [audioOnlyHoldCueId, setAudioOnlyHoldCueId] = useState<Id | null>(null);
  const keepCueIds = useMemo(() => {
    if (!audioOnly) return undefined;
    const ids = new Set<Id>();
    if (pendingFocus?.cueId) ids.add(pendingFocus.cueId);
    if (focusCell?.cueId) ids.add(focusCell.cueId);
    if (audioOnlyHoldCueId) ids.add(audioOnlyHoldCueId);
    return ids;
  }, [audioOnly, audioOnlyHoldCueId, focusCell?.cueId, pendingFocus?.cueId]);
  const rows = useMemo(
    () => layoutCues(cues, { audioOnly, keepCueIds }),
    [audioOnly, cues, keepCueIds],
  );
  const navOrder = useMemo(
    () => buildNavOrder(cues, columnOrder, keepCueIds),
    [columnOrder, cues, keepCueIds],
  );
  const editorsRef = useRef(new Map<CellKey, Editor>());
  const tableRef = useRef<HTMLTableElement>(null);
  const tableShellRef = useRef<HTMLDivElement>(null);
  const cuesRef = useRef(cues);
  cuesRef.current = cues;
  /**
   * Cue ids confirmed for delete. Blocks blur/onChange flushes from TipTap
   * cells that are unmounting so a stale `cue.upsert` cannot bump the server
   * document and resurrect the row via merge.
   */
  const suppressPersistCueIdsRef = useRef(new Set<Id>());
  const onPickSplitAtRef = useRef(onPickSplitAt);
  onPickSplitAtRef.current = onPickSplitAt;
  const [splitHover, setSplitHover] = useState<{
    index: number;
    top: number;
  } | null>(null);
  const [cellSelection, setCellSelection] = useState<CellSelectionRange | null>(
    null,
  );
  const cellSelectionRef = useRef(cellSelection);
  cellSelectionRef.current = cellSelection;
  const lastCellRef = useRef<CellRef | null>(null);
  const handleDeleteSelectionRef = useRef<() => boolean>(() => false);
  const cellDragRef = useRef<{
    anchor: CellRef;
    multi: boolean;
    pointerId: number;
  } | null>(null);
  const [resizePreview, setResizePreview] = useState<{
    cueId: Id;
    span: number;
    side: SpanConflictSide;
  } | null>(null);
  const [spanGhost, setSpanGhost] = useState<SpanGhostRect | null>(null);
  const [spanConflict, setSpanConflict] = useState<{
    cueId: Id;
    span: number;
    previews: string[];
    side: SpanConflictSide;
  } | null>(null);
  const [dragCueId, setDragCueId] = useState<Id | null>(null);
  const [dropBeforeCueId, setDropBeforeCueId] = useState<Id | null | undefined>(
    undefined,
  );
  const [dropLineTop, setDropLineTop] = useState<number | null>(null);
  const endRowDragRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!audioOnly) setAudioOnlyHoldCueId(null);
  }, [audioOnly]);

  // Undo / remote restore can bring a cue id back — allow persist again.
  useEffect(() => {
    const live = new Set(cues.map((c) => c.id));
    for (const id of [...suppressPersistCueIdsRef.current]) {
      if (live.has(id)) suppressPersistCueIdsRef.current.delete(id);
    }
  }, [cues]);

  useEffect(() => {
    if (focusCell) setPendingFocus(focusCell);
  }, [focusCell]);

  const prevContentResetKeyRef = useRef(contentResetKey);
  useLayoutEffect(() => {
    if (prevContentResetKeyRef.current === contentResetKey) return;
    prevContentResetKeyRef.current = contentResetKey;
    setPendingFocus(null);
  }, [contentResetKey]);

  useEffect(() => {
    return () => {
      endRowDragRef.current?.();
      document.body.classList.remove(
        "sm-is-span-resizing",
        "sm-is-row-dragging",
        "sm-is-cell-selecting",
      );
    };
  }, []);

  /**
   * Stretch single-row .sm-cue-cell to fill its <td>. Spanned cells stay in
   * normal flow so extra lines grow the table instead of overlapping the
   * next row. Never copy a rowspan cell’s height onto the other column.
   * Measure against natural (unstretched) row height so meta rows cannot
   * feed a ResizeObserver loop.
   *
   * Do not depend on `cues` (or other focus-driven props). Focusing a cell
   * writes a lock, which replaces `cues`; tearing this effect down would
   * clear minHeight before paint and restore it a frame later — a visible
   * jump even though the height did not change.
   */
  useLayoutEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    const clearFill = (cell: HTMLElement) => {
      cell.style.minHeight = "";
      cell.style.position = "";
      cell.style.left = "";
      cell.style.right = "";
      cell.style.top = "";
      cell.style.bottom = "";
    };

    let raf = 0;
    let syncing = false;
    const applyFill = () => {
      syncing = true;
      try {
        const tds = table.querySelectorAll<HTMLTableCellElement>(
          "td.sm-cue-visual-cell, td.sm-cue-audio-cell",
        );
        const pairs: {
          td: HTMLTableCellElement;
          cell: HTMLElement;
        }[] = [];
        tds.forEach((td) => {
          const cell = td.querySelector<HTMLElement>(":scope > .sm-cue-cell");
          if (!cell) return;
          pairs.push({ td, cell });
        });

        for (const { td, cell } of pairs) {
          if (td.rowSpan > 1) clearFill(cell);
          else cell.style.minHeight = "";
        }
        void table.offsetHeight;

        for (const { td, cell } of pairs) {
          if (td.rowSpan > 1) continue;
          const fill = remainingCellFillPx(td, cell);
          const next = fill > 0 ? `${fill}px` : "";
          if (cell.style.minHeight !== next) cell.style.minHeight = next;
        }
      } finally {
        syncing = false;
      }
    };

    const sync = () => {
      if (syncing) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(applyFill);
    };

    const ro = new ResizeObserver(sync);
    ro.observe(table);
    const mo = new MutationObserver((mutations) => {
      if (!mutationsAffectCellLayout(mutations)) return;
      sync();
    });
    mo.observe(table, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["rowspan"],
    });
    applyFill();
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      mo.disconnect();
    };
  }, []);

  const selectedKeys = useMemo(() => {
    if (!cellSelection) return new Set<CellKey>();
    return new Set(
      cellsInRange(rows, cellSelection.anchor, cellSelection.focus).map((c) =>
        cellKey(c.cueId, c.column),
      ),
    );
  }, [cellSelection, rows]);

  const collapseEditorSelections = useCallback(() => {
    window.getSelection()?.removeAllRanges();
    for (const ed of editorsRef.current.values()) {
      const { from } = ed.state.selection;
      if (!ed.state.selection.empty) {
        ed.commands.setTextSelection(from);
      }
    }
  }, []);

  const endCellDrag = useCallback(() => {
    cellDragRef.current = null;
    document.body.classList.remove("sm-is-cell-selecting");
  }, []);

  const nearestSplitLine = useCallback((clientY: number) => {
    const table = tableRef.current;
    const shell = tableShellRef.current;
    if (!table || !shell) return null;
    const lastIndex = cuesRef.current.length - 1;
    if (lastIndex < 1) return null;
    const shellRect = shell.getBoundingClientRect();
    const rowEls = table.querySelectorAll<HTMLElement>("tbody tr.sm-cue-row");
    let best: { index: number; dist: number; top: number } | null = null;
    for (const tr of rowEls) {
      const index = Number(tr.dataset.cueIndex);
      if (!Number.isFinite(index) || index < 0 || index >= lastIndex) {
        continue;
      }
      const rect = tr.getBoundingClientRect();
      const dist = Math.abs(clientY - rect.bottom);
      if (!best || dist < best.dist) {
        best = {
          index,
          dist,
          top: rect.bottom - shellRect.top,
        };
      }
    }
    if (!best || best.dist > 18) return null;
    return best;
  }, []);

  const handleSplitPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLTableElement>) => {
      if (!splitPickMode) return;
      const hit = nearestSplitLine(e.clientY);
      setSplitHover(hit ? { index: hit.index, top: hit.top } : null);
    },
    [nearestSplitLine, splitPickMode],
  );

  const handleTablePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLTableElement>) => {
      noteScriptPointer();
      if (splitPickMode) {
        if (e.button !== 0) return;
        e.preventDefault();
        const hit = nearestSplitLine(e.clientY);
        const next = hit ? cuesRef.current[hit.index + 1] : undefined;
        if (next) onPickSplitAtRef.current?.(next.id);
        return;
      }
      if (!cellSelectMode) return;
      if (e.button !== 0) return;
      if (isCellSelectChrome(e.target)) return;
      const cell = parseCellElement(e.target as Element);
      if (!cell || !tableRef.current?.contains(e.target as Node)) return;

      const prevLast = lastCellRef.current;
      lastCellRef.current = cell;

      if (e.shiftKey) {
        const anchor =
          cellSelectionRef.current?.anchor ?? prevLast ?? cell;
        setCellSelection({ anchor, focus: cell });
        collapseEditorSelections();
        e.preventDefault();
        return;
      }

      e.preventDefault();
      collapseEditorSelections();
      cellDragRef.current = {
        anchor: cell,
        multi: false,
        pointerId: e.pointerId,
      };

      const onMove = (ev: PointerEvent) => {
        const drag = cellDragRef.current;
        if (!drag || ev.pointerId !== drag.pointerId) return;
        const focus = resolveCellFromPoint(ev.clientX, ev.clientY);
        if (!focus) return;
        if (sameCell(focus, drag.anchor)) {
          if (!drag.multi) return;
          setCellSelection({ anchor: drag.anchor, focus });
          return;
        }
        if (!drag.multi) {
          drag.multi = true;
          document.body.classList.add("sm-is-cell-selecting");
          collapseEditorSelections();
        }
        lastCellRef.current = focus;
        setCellSelection({ anchor: drag.anchor, focus });
      };

      const onUp = (ev: PointerEvent) => {
        if (ev.pointerId !== cellDragRef.current?.pointerId) return;
        const drag = cellDragRef.current;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        if (drag && !drag.multi) {
          setCellSelection(null);
        }
        endCellDrag();
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [
      cellSelectMode,
      collapseEditorSelections,
      endCellDrag,
      nearestSplitLine,
      splitPickMode,
    ],
  );

  useEffect(() => {
    if (!splitPickMode) setSplitHover(null);
  }, [splitPickMode]);

  useEffect(() => {
    if (cellSelectMode) return;
    setCellSelection(null);
    document.body.classList.remove("sm-is-cell-selecting");
  }, [cellSelectMode]);

  useEffect(() => {
    const onCopy = (e: ClipboardEvent) => {
      const sel = cellSelectionRef.current;
      if (!sel) return;
      const cells = cellsInRange(rows, sel.anchor, sel.focus);
      if (cells.length < 2) return;
      const text = formatSelectionPlainText(
        cells,
        rows,
        (cueId, column) => {
          const ed = editorsRef.current.get(cellKey(cueId, column));
          if (ed) return editorPlainText(ed);
          const cue = cues.find((c) => c.id === cueId);
          return cue
            ? cueContentPlainText(getCueSide(cue, column).content)
            : "";
        },
        columnOrder,
      );
      e.preventDefault();
      e.clipboardData?.setData("text/plain", text);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && cellSelectionRef.current) {
        setCellSelection(null);
        return;
      }
      if (e.key === "Backspace" || e.key === "Delete") {
        if (handleDeleteSelectionRef.current()) {
          e.preventDefault();
        }
      }
    };
    window.addEventListener("copy", onCopy, true);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("copy", onCopy, true);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [columnOrder, cues, rows]);

  const updateResizePreview = useCallback(
    (
      cueId: Id,
      ownerIndex: number,
      span: number,
      side: SpanConflictSide,
    ) => {
      setResizePreview({ cueId, span, side });
      const table = tableRef.current;
      if (!table) {
        setSpanGhost(null);
        return;
      }
      setSpanGhost(measureSpanGhost(table, ownerIndex, span, side));
    },
    [],
  );

  const clearResizePreview = useCallback(() => {
    setResizePreview(null);
    setSpanGhost(null);
  }, []);

  useEffect(() => {
    if (spanEdit) return;
    clearResizePreview();
    document.body.classList.remove("sm-is-span-resizing");
  }, [clearResizePreview, spanEdit]);

  const requestVisualSpan = useCallback(
    (cueId: Id, span: number) => {
      const index = cues.findIndex((c) => c.id === cueId);
      if (index < 0) return;
      const prevSpan = Math.max(1, cues[index]!.visualRowSpan ?? 1);
      const maxSpan = cues.length - index;
      const nextSpan = Math.max(1, Math.min(Math.floor(span), maxSpan));
      if (nextSpan === prevSpan) return;
      const previews = coveredSidePreviews(
        cues,
        index,
        prevSpan,
        nextSpan,
        "visual",
      );
      if (previews.length > 0) {
        setSpanConflict({ cueId, span: nextSpan, previews, side: "visual" });
        return;
      }
      onSetVisualSpan(cueId, nextSpan);
    },
    [cues, onSetVisualSpan],
  );

  const requestAudioSpan = useCallback(
    (cueId: Id, span: number) => {
      const index = cues.findIndex((c) => c.id === cueId);
      if (index < 0) return;
      const prevSpan = Math.max(1, cues[index]!.audioRowSpan ?? 1);
      const maxSpan = cues.length - index;
      const nextSpan = Math.max(1, Math.min(Math.floor(span), maxSpan));
      if (nextSpan === prevSpan) return;
      const previews = coveredSidePreviews(
        cues,
        index,
        prevSpan,
        nextSpan,
        "audio",
      );
      if (previews.length > 0) {
        setSpanConflict({ cueId, span: nextSpan, previews, side: "audio" });
        return;
      }
      onSetAudioSpan(cueId, nextSpan);
    },
    [cues, onSetAudioSpan],
  );

  const spanFromPointerY = useCallback(
    (ownerIndex: number, clientY: number) => {
      const table = tableRef.current;
      if (!table) return 1;
      const rowEls = [
        ...table.querySelectorAll<HTMLElement>("tr.sm-cue-row[data-cue-index]"),
      ];
      let bestIndex = ownerIndex;
      for (const el of rowEls) {
        const rect = el.getBoundingClientRect();
        const mid = rect.top + rect.height / 2;
        if (clientY >= mid) {
          const idx = Number(el.dataset.cueIndex);
          if (Number.isFinite(idx) && idx >= ownerIndex) {
            bestIndex = idx;
          }
        }
      }
      return Math.max(1, bestIndex - ownerIndex + 1);
    },
    [],
  );

  const beginVisualResize = useCallback(
    (cueId: Id, ownerIndex: number, e: ReactPointerEvent<HTMLElement>) => {
      if (readOnly || !spanEdit) return;
      e.preventDefault();
      e.stopPropagation();
      const handle = e.currentTarget;
      handle.setPointerCapture(e.pointerId);
      handle.classList.add("is-resizing");
      document.body.classList.add("sm-is-span-resizing");
      const maxSpan = cues.length - ownerIndex;

      const onMove = (ev: PointerEvent) => {
        const span = Math.min(
          maxSpan,
          spanFromPointerY(ownerIndex, ev.clientY),
        );
        updateResizePreview(cueId, ownerIndex, span, "visual");
      };
      const onUp = (ev: PointerEvent) => {
        handle.releasePointerCapture(ev.pointerId);
        handle.removeEventListener("pointermove", onMove);
        handle.removeEventListener("pointerup", onUp);
        handle.removeEventListener("pointercancel", onUp);
        handle.classList.remove("is-resizing");
        document.body.classList.remove("sm-is-span-resizing");
        const span = Math.min(
          maxSpan,
          spanFromPointerY(ownerIndex, ev.clientY),
        );
        clearResizePreview();
        requestVisualSpan(cueId, span);
      };
      handle.addEventListener("pointermove", onMove);
      handle.addEventListener("pointerup", onUp);
      handle.addEventListener("pointercancel", onUp);
      updateResizePreview(
        cueId,
        ownerIndex,
        Math.max(1, cues[ownerIndex]!.visualRowSpan ?? 1),
        "visual",
      );
    },
    [
      clearResizePreview,
      cues,
      readOnly,
      requestVisualSpan,
      spanEdit,
      spanFromPointerY,
      updateResizePreview,
    ],
  );

  const beginAudioResize = useCallback(
    (cueId: Id, ownerIndex: number, e: ReactPointerEvent<HTMLElement>) => {
      if (readOnly || !spanEdit) return;
      e.preventDefault();
      e.stopPropagation();
      const handle = e.currentTarget;
      handle.setPointerCapture(e.pointerId);
      handle.classList.add("is-resizing");
      document.body.classList.add("sm-is-span-resizing");
      const maxSpan = cues.length - ownerIndex;

      const onMove = (ev: PointerEvent) => {
        const span = Math.min(
          maxSpan,
          spanFromPointerY(ownerIndex, ev.clientY),
        );
        updateResizePreview(cueId, ownerIndex, span, "audio");
      };
      const onUp = (ev: PointerEvent) => {
        handle.releasePointerCapture(ev.pointerId);
        handle.removeEventListener("pointermove", onMove);
        handle.removeEventListener("pointerup", onUp);
        handle.removeEventListener("pointercancel", onUp);
        handle.classList.remove("is-resizing");
        document.body.classList.remove("sm-is-span-resizing");
        const span = Math.min(
          maxSpan,
          spanFromPointerY(ownerIndex, ev.clientY),
        );
        clearResizePreview();
        requestAudioSpan(cueId, span);
      };
      handle.addEventListener("pointermove", onMove);
      handle.addEventListener("pointerup", onUp);
      handle.addEventListener("pointercancel", onUp);
      updateResizePreview(
        cueId,
        ownerIndex,
        Math.max(1, cues[ownerIndex]!.audioRowSpan ?? 1),
        "audio",
      );
    },
    [
      clearResizePreview,
      cues,
      readOnly,
      requestAudioSpan,
      spanEdit,
      spanFromPointerY,
      updateResizePreview,
    ],
  );

  const beginRowDrag = useCallback(
    (cueId: Id, e: ReactPointerEvent<HTMLElement>) => {
      if (readOnly || e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      let latestBefore: Id | null = null;
      let moved = false;
      let finished = false;
      let raf = 0;
      const startY = e.clientY;

      const finish = (apply: boolean) => {
        if (finished) return;
        finished = true;
        endRowDragRef.current = null;
        if (raf) cancelAnimationFrame(raf);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        window.removeEventListener("blur", onBlur);
        document.body.classList.remove("sm-is-row-dragging");
        setDragCueId(null);
        setDropBeforeCueId(undefined);
        setDropLineTop(null);
        if (apply && moved) onMoveCueBlock(cueId, latestBefore);
      };

      const onMove = (ev: PointerEvent) => {
        if (Math.abs(ev.clientY - startY) > 5) {
          if (!moved) {
            moved = true;
            document.body.classList.add("sm-is-row-dragging");
            setDragCueId(cueId);
          }
        }
        if (!moved) return;
        ev.preventDefault();
        const table = tableRef.current;
        const shell = tableShellRef.current;
        if (!table || !shell) return;
        const shellRect = shell.getBoundingClientRect();
        const scaleY =
          shellRect.height > 0 ? shell.offsetHeight / shellRect.height : 1;
        const rowEls = [
          ...table.querySelectorAll<HTMLElement>("tbody tr.sm-cue-row"),
        ];
        const seen = new Set<string>();
        const blocks: { id: string; top: number; bottom: number }[] = [];
        for (const el of rowEls) {
          const blockId = el.dataset.blockCueId;
          if (!blockId || blockId === cueId) continue;
          const rect = el.getBoundingClientRect();
          if (!seen.has(blockId)) {
            seen.add(blockId);
            blocks.push({ id: blockId, top: rect.top, bottom: rect.bottom });
          } else {
            const last = blocks[blocks.length - 1];
            if (last && last.id === blockId) last.bottom = rect.bottom;
          }
        }
        let before: Id | null = null;
        let lineY =
          rowEls.at(-1)?.getBoundingClientRect().bottom ?? shellRect.bottom;
        for (const block of blocks) {
          if (ev.clientY < (block.top + block.bottom) / 2) {
            before = block.id;
            lineY = block.top;
            break;
          }
        }
        if (before === null) {
          const last = blocks.at(-1);
          if (last) lineY = last.bottom;
        }
        latestBefore = before;
        const top = (lineY - shellRect.top) * scaleY;
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          raf = 0;
          setDropBeforeCueId(before);
          setDropLineTop(top);
        });
      };

      const onUp = () => finish(true);
      const onBlur = () => finish(false);

      window.addEventListener("pointermove", onMove, { passive: false });
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
      window.addEventListener("blur", onBlur);
      endRowDragRef.current = () => finish(false);
    },
    [onMoveCueBlock, readOnly],
  );
  const registerEditor = useCallback(
    (cueId: Id, column: ScriptColumn, editor: Editor | null) => {
      const key = cellKey(cueId, column);
      if (editor) editorsRef.current.set(key, editor);
      else editorsRef.current.delete(key);
    },
    [],
  );

  const getLiveContent = useCallback(
    (cueId: Id, column: ScriptColumn) => {
      const ed = editorsRef.current.get(cellKey(cueId, column));
      if (ed) return editorHtml(ed);
      const cue = cues.find((c) => c.id === cueId);
      return cue ? getCueSide(cue, column).content : "";
    },
    [cues],
  );

  const handleFocus = useCallback(
    (cueId: Id, column: ScriptColumn) => {
      if (audioOnly) setAudioOnlyHoldCueId(cueId);
      onFocus(cueId, column);
      const ed = editorsRef.current.get(cellKey(cueId, column)) ?? null;
      onActiveEditorChange?.(ed);
    },
    [audioOnly, onActiveEditorChange, onFocus],
  );

  const handleBlur = useCallback(
    (cueId: Id, column: ScriptColumn) => {
      if (suppressPersistCueIdsRef.current.has(cueId)) {
        window.setTimeout(() => {
          setAudioOnlyHoldCueId((cur) => (cur === cueId ? null : cur));
          onActiveEditorChange?.(null);
        }, 0);
        return;
      }
      onBlur(cueId, column);
      window.setTimeout(() => {
        const active = document.activeElement;
        if (active?.closest(".sm-format-toolbar")) return;
        if (active?.closest(".sm-row-add")) return;
        if (active?.closest(".sm-episode-top")) return;
        if (active?.closest(".ProseMirror")) return;
        setAudioOnlyHoldCueId((cur) => (cur === cueId ? null : cur));
        onActiveEditorChange?.(null);
      }, 0);
    },
    [onActiveEditorChange, onBlur],
  );

  const handleChange = useCallback(
    (cueId: Id, column: ScriptColumn, content: string) => {
      if (suppressPersistCueIdsRef.current.has(cueId)) return;
      onChange(cueId, column, content);
    },
    [onChange],
  );

  const handleNewPair = useCallback(
    (afterCueId: Id, column: ScriptColumn, flushedContent: string) => {
      // Always insert after the cell that fired Return, and flush that same
      // cell. A previous “continue after last created row” chain wrote the
      // current cell’s text into a different row (copies) and, after a
      // reorder, inserted far from the caret.
      const newId = onNewPair(afterCueId, column, flushedContent);
      if (newId) {
        if (audioOnly) setAudioOnlyHoldCueId(newId);
        setPendingFocus({ cueId: newId, column });
      }
    },
    [audioOnly, onNewPair],
  );

  const handleInsertRowAfter = useCallback(
    (afterCueId: Id) => {
      if (readOnly) return;
      const column =
        lastCellRef.current?.cueId === afterCueId
          ? lastCellRef.current.column
          : leftColumn;
      const flushed = getLiveContent(afterCueId, column);
      const newId = onNewPair(afterCueId, column, flushed);
      if (newId) {
        if (audioOnly) setAudioOnlyHoldCueId(newId);
        setPendingFocus({ cueId: newId, column });
      }
    },
    [audioOnly, getLiveContent, leftColumn, onNewPair, readOnly],
  );

  const handleInsertRowBefore = useCallback(
    (beforeCueId: Id) => {
      if (readOnly) return;
      const column =
        lastCellRef.current?.cueId === beforeCueId
          ? lastCellRef.current.column
          : leftColumn;
      const flushed = getLiveContent(beforeCueId, column);
      const newId = onNewPair(beforeCueId, column, flushed, "before");
      if (newId) {
        if (audioOnly) setAudioOnlyHoldCueId(newId);
        setPendingFocus({ cueId: newId, column });
      }
    },
    [audioOnly, getLiveContent, leftColumn, onNewPair, readOnly],
  );

  const handleNavigate = useCallback(
    (from: CellRef, dir: NavDir) => {
      const idx = navOrder.findIndex(
        (c) => c.cueId === from.cueId && c.column === from.column,
      );
      if (idx < 0) return;

      let next: CellRef | null = null;
      if (dir === "left" || dir === "right") {
        if (audioOnly || rightColumn == null) {
          const step = dir === "right" ? 1 : -1;
          next = navOrder[idx + step] ?? null;
        } else {
          const toward = dir === "left" ? leftColumn : rightColumn;
          const fromIsEdge =
            (dir === "left" && from.column === rightColumn) ||
            (dir === "right" && from.column === leftColumn);
          if (fromIsEdge) {
            const ownerId = findSpanOwnerId(cues, from.cueId, toward);
            next = ownerId
              ? { cueId: ownerId, column: toward }
              : (navOrder[idx + (dir === "right" ? 1 : -1)] ?? null);
          } else {
            const step = dir === "right" ? 1 : -1;
            next = navOrder[idx + step] ?? null;
          }
        }
      } else {
        const step = dir === "down" ? 1 : -1;
        for (let i = idx + step; i >= 0 && i < navOrder.length; i += step) {
          if (navOrder[i]!.column === from.column) {
            next = navOrder[i]!;
            break;
          }
        }
      }
      if (next) {
        setCellSelection(null);
        setPendingFocus(next);
      }
    },
    [audioOnly, cues, leftColumn, navOrder, rightColumn],
  );

  const handleDeletePairSingle = useCallback(
    (cueId: Id) => {
      if (readOnly || cues.length <= 1) return;
      const cue = cues.find((c) => c.id === cueId);
      if (!cue) return;

      const visual = getLiveContent(cueId, "visual");
      const audio = getLiveContent(cueId, "audio");
      const empty = isCueContentEmpty(cue, visual, audio);
      const linkedWarnings = listCueLinkedWarnings(cue, comments, noteColumns);
      const lockWarning = formatCueLockWarningBlock(cue, userId);

      if (empty) {
        if (linkedWarnings.length > 0 || lockWarning) {
          const ok = window.confirm(
            buildCueDeleteConfirmMessage({
              emptyManuscript: true,
              linkedWarnings,
              lockWarning,
            }),
          );
          if (!ok) return;
        } else if (confirmDeleteEmptyPair) {
          const ok = window.confirm("Slet denne tomme række?");
          if (!ok) return;
        }
      } else {
        const preview = formatCueDeletePreview(cue, visual, audio);
        const ok = window.confirm(
          buildCueDeleteConfirmMessage({
            emptyManuscript: false,
            preview,
            linkedWarnings,
            lockWarning,
          }),
        );
        if (!ok) return;
      }

      const idx = navOrder.findIndex((c) => c.cueId === cueId);
      const focusTarget =
        (idx > 0 ? navOrder[idx - 1] : null) ??
        navOrder.find((c) => c.cueId !== cueId) ??
        null;
      setCellSelection(null);
      // Mark before any focus/blur churn so TipTap cannot flush a stale upsert.
      suppressPersistCueIdsRef.current.add(cueId);
      // Delete synchronously: deferring past blur used to let cue.upsert race
      // the full-document save and resurrect the row via merge.
      onDeletePair(cueId);
      if (focusTarget && focusTarget.cueId !== cueId) {
        setPendingFocus(focusTarget);
      }
    },
    [
      comments,
      confirmDeleteEmptyPair,
      cues,
      getLiveContent,
      navOrder,
      noteColumns,
      onDeletePair,
      readOnly,
      userId,
    ],
  );

  const handleDeletePairs = useCallback(
    (cueIds: Id[]) => {
      if (readOnly || cueIds.length === 0) return;
      const unique = cues.map((c) => c.id).filter((id) => cueIds.includes(id));
      if (unique.length === 0) return;

      /* Always keep at least one cue in the scene. */
      const maxDeletable = Math.max(0, cues.length - 1);
      const toDelete = unique.slice(0, maxDeletable);
      if (toDelete.length === 0) {
        window.alert("Der skal være mindst én række i scenen.");
        return;
      }

      if (toDelete.length === 1) {
        handleDeletePairSingle(toDelete[0]!);
        return;
      }

      const keptNote =
        unique.length > toDelete.length
          ? "\n\n(Mindst én række behaltes.)"
          : "";
      const deleteCues = toDelete
        .map((id) => cues.find((c) => c.id === id))
        .filter((c): c is Cue => Boolean(c));
      const previews = toDelete.slice(0, 5).map((id) => {
        const cue = cues.find((c) => c.id === id)!;
        return formatCueDeletePreview(
          cue,
          getLiveContent(id, "visual"),
          getLiveContent(id, "audio"),
        );
      });
      const more =
        toDelete.length > 5 ? `\n\n…og ${toDelete.length - 5} mere` : "";
      const linked = formatLinkedWarningsBlock(
        listCuesLinkedWarningsSummary(deleteCues, comments, noteColumns),
      );
      const lockWarning = formatCuesLockWarningBlock(deleteCues, userId);
      const ok = window.confirm(
        `Vil du slette ${toDelete.length} rækker i manus?\n\n${previews.join("\n\n—\n\n")}${more}${keptNote}${linked}${lockWarning}`,
      );
      if (!ok) return;

      const deleteSet = new Set(toDelete);
      const focusTarget =
        navOrder.find((c) => !deleteSet.has(c.cueId)) ?? null;
      setCellSelection(null);

      for (const id of toDelete) {
        suppressPersistCueIdsRef.current.add(id);
      }
      // Synchronous delete — see handleDeletePairSingle.
      if (onDeletePairs) {
        onDeletePairs(toDelete);
      } else {
        for (const id of [...toDelete].reverse()) {
          onDeletePair(id);
        }
      }
      if (focusTarget) setPendingFocus(focusTarget);
    },
    [
      comments,
      cues,
      getLiveContent,
      handleDeletePairSingle,
      navOrder,
      noteColumns,
      onDeletePair,
      onDeletePairs,
      readOnly,
      userId,
    ],
  );

  const handleDeletePair = useCallback(
    (cueId: Id) => {
      const sel = cellSelectionRef.current;
      if (sel) {
        const ids = cueIdsInSelection(cues, rows, sel);
        if (ids.length > 1 && ids.includes(cueId)) {
          handleDeletePairs(ids);
          return;
        }
      }
      handleDeletePairSingle(cueId);
    },
    [cues, handleDeletePairSingle, handleDeletePairs, rows],
  );

  const handleDeleteSelection = useCallback((): boolean => {
    const sel = cellSelectionRef.current;
    if (!sel) return false;
    const cells = cellsInRange(rows, sel.anchor, sel.focus);
    if (cells.length < 2) return false;
    const ids = cueIdsInSelection(cues, rows, sel);
    if (ids.length === 0) return false;
    handleDeletePairs(ids);
    return true;
  }, [cues, handleDeletePairs, rows]);
  handleDeleteSelectionRef.current = handleDeleteSelection;

  const commentsByCell = useCallback(
    (cueId: Id, column: ScriptColumn) =>
      comments.filter(
        (c) => c.cueId === cueId && c.column === column && !c.resolved,
      ),
    [comments],
  );

  const handleAddComment = useCallback(
    (
      cueId: Id,
      column: ScriptColumn,
      body: string,
      range?: { from: number; to: number; quote: string },
    ) => {
      onAddComment?.(cueId, column, body, range);
    },
    [onAddComment],
  );

  const handleResolveComment = useCallback(
    (commentId: Id) => {
      onResolveComment?.(commentId);
    },
    [onResolveComment],
  );

  const handleRemoveComment = useCallback(
    (commentId: Id) => {
      onRemoveComment?.(commentId);
    },
    [onRemoveComment],
  );

  const handleSetCharacter = useCallback(
    (cueId: Id, column: ScriptColumn, characterName: string | null) => {
      onSetCharacter?.(cueId, column, characterName);
    },
    [onSetCharacter],
  );

  const sideEditorProps = {
    userId,
    readOnly,
    canDeletePair: !readOnly && cues.length > 1,
    castMembers,
    showComments,
    onFocus: (cueId: Id, column: ScriptColumn) => {
      lastCellRef.current = { cueId, column };
      handleFocus(cueId, column);
    },
    onBlur: handleBlur,
    onChange: handleChange,
    onNewPair: handleNewPair,
    onDeletePair: handleDeletePair,
    onDeleteSelection: handleDeleteSelection,
    onAddComment: handleAddComment,
    onResolveComment: handleResolveComment,
    onRemoveComment: handleRemoveComment,
    onSetCharacter: handleSetCharacter,
    onNavigate: handleNavigate,
    getLiveContent,
    registerEditor,
    newRowOnDoubleReturn,
    clickSelectsCell: clickSelectsCell && !cellSelectMode,
    shiftClickSelectsCell: !cellSelectMode,
    contentResetKey,
  };

  return (
    <div className="sm-dual">
      <div className="sm-dual-table-shell" ref={tableShellRef}>
      <table
        ref={tableRef}
        className={`sm-dual-table${
          showColumnRule && !audioOnly ? " sm-col-rule" : ""
        }${dragCueId ? " is-reordering" : ""}${
          resizePreview ? " is-resizing-span" : ""
        }${
          cellSelectMode ? " is-cell-select-mode" : ""
        }${
          spanEdit ? " is-row-span-mode" : ""
        }${
          cellSelection && selectedKeys.size > 1 ? " has-cell-selection" : ""
        }${audioOnly ? " sm-audio-only" : visualIsLeft ? " sm-visual-left" : " sm-audio-left"}${
          splitPickMode
            ? ` is-split-pick${splitHover ? " is-split-aiming" : ""}`
            : rowRuleHitInGutter && !audioOnly
              ? " sm-row-rule-gutter-hit"
              : ""
        }`}
        onPointerDown={handleTablePointerDown}
        onPointerMove={handleSplitPointerMove}
        onPointerLeave={() => setSplitHover(null)}
      >
        <thead>
          <tr>
            {(audioOnly
              ? (["audio"] as const)
              : visualIsLeft
                ? (["visual", "audio"] as const)
                : (["audio", "visual"] as const)
            ).map((col) => (
              <th key={col} scope="col" data-column={col}>
                {col === "visual" ? "Billedside" : "Lydside"}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ cue, index, visualSpan, audioSpan, blockCueId }) => {
            const isBlockOwner = blockCueId === cue.id;
            const previewSpan =
              resizePreview?.cueId === cue.id ? resizePreview.span : null;
            const previewSide =
              resizePreview?.cueId === cue.id ? resizePreview.side : null;
            const effectiveVisualSpan =
              previewSide === "visual" && previewSpan != null
                ? previewSpan
                : visualSpan;
            const effectiveAudioSpan =
              previewSide === "audio" && previewSpan != null
                ? previewSpan
                : audioSpan;

            const resizeOwnerIndex =
              resizePreview != null
                ? cues.findIndex((c) => c.id === resizePreview.cueId)
                : -1;
            const inResizeCover =
              resizePreview != null &&
              resizeOwnerIndex >= 0 &&
              index > resizeOwnerIndex &&
              index < resizeOwnerIndex + resizePreview.span;

            const dropClass = reorderDropClass(
              dragCueId,
              dropBeforeCueId,
              cue.id,
              index === rows.length - 1,
            );

            const nextCueId =
              index < cues.length - 1 ? cues[index + 1]!.id : null;
            const showSplitHit = Boolean(
              splitPickMode && onPickSplitAt && nextCueId,
            );
            const showRuleHit =
              !splitPickMode && !readOnly && rowRuleMode === "custom";
            const gutterHit =
              !splitPickMode && rowRuleHitInGutter && !audioOnly;
            const ruleOnVisual =
              (showSplitHit || showRuleHit) && visualSpan === 1;
            const ruleOnAudio =
              (showSplitHit || showRuleHit) && audioSpan === 1;
            const ruleExtend =
              gutterHit || audioOnly
                ? null
                : ruleOnVisual && !ruleOnAudio
                ? visualIsLeft
                  ? "extend-right"
                  : "extend-left"
                : ruleOnAudio && !ruleOnVisual
                  ? visualIsLeft
                    ? "extend-left"
                    : "extend-right"
                  : null;

            let visualRuleEnd = false;
            if (visualSpan > 1) {
              const endCue = cues[index + visualSpan - 1];
              visualRuleEnd = cueShowsRowRule(endCue, rowRuleMode);
            } else if (visualSpan === 1) {
              visualRuleEnd = cueShowsRowRule(cue, rowRuleMode);
            }

            let audioRuleEnd = false;
            if (audioSpan > 1) {
              const endCue = cues[index + audioSpan - 1];
              audioRuleEnd = cueShowsRowRule(endCue, rowRuleMode);
            }

            const ruleBelow = cueShowsRowRule(cue, rowRuleMode);
            const leftSpan = leftColumn === "visual" ? visualSpan : audioSpan;
            const rightSpan =
              rightColumn == null
                ? 0
                : rightColumn === "visual"
                  ? visualSpan
                  : audioSpan;
            const showGripOnLeft =
              isBlockOwner && !readOnly && leftSpan > 0;
            const showDeleteOnRight =
              !readOnly && rightColumn != null && rightSpan > 0;
            const showDeleteOnLeft =
              !readOnly && leftSpan > 0 && rightSpan === 0;

            const showCol1Meta = showSetupNumbers || showCameraNotes;
            const showMetaRow = showCol1Meta || showPosition;
            const bothCellsThisCue =
              !audioOnly && leftSpan > 0 && rightSpan > 0;
            const metaChange =
              onCameraMetaChange ?? (() => undefined);

            const col1MetaEl =
              showMetaRow &&
              (showCol1Meta || (showPosition && bothCellsThisCue)) ? (
                <div
                  className={
                    showCol1Meta
                      ? "sm-cue-meta-row"
                      : "sm-cue-meta-row sm-cue-meta-row-spacer"
                  }
                  aria-hidden={!showCol1Meta}
                >
                  {showSetupNumbers ? (
                    <span
                      className="sm-setup-num"
                      aria-label={`Indstilling ${sceneNumber}-${index + 1}`}
                    >
                      {sceneNumber}-{index + 1}
                    </span>
                  ) : null}
                  {showCameraNotes ? (
                    <CameraNoteLine
                      cueId={cue.id}
                      cameraNumber={cue.cameraNumber}
                      cameraNote={cue.cameraNote}
                      readOnly={readOnly}
                      onChange={metaChange}
                    />
                  ) : null}
                  {!showCol1Meta ? (
                    <PositionNoteSpacer positionNote={cue.positionNote} />
                  ) : null}
                </div>
              ) : null;

            const col2MetaEl =
              showMetaRow &&
              (showPosition || (showCol1Meta && bothCellsThisCue)) ? (
                <div
                  className={
                    showPosition
                      ? "sm-cue-meta-row"
                      : "sm-cue-meta-row sm-cue-meta-row-spacer"
                  }
                  aria-hidden={!showPosition}
                >
                  {showPosition ? (
                    <PositionNoteLine
                      cueId={cue.id}
                      positionNote={cue.positionNote}
                      readOnly={readOnly}
                      onChange={metaChange}
                    />
                  ) : (
                    <>
                      {showSetupNumbers ? (
                        <span className="sm-setup-num">
                          {sceneNumber}-{index + 1}
                        </span>
                      ) : null}
                      {showCameraNotes ? (
                        <CameraNoteSpacer
                          cameraNumber={cue.cameraNumber}
                          cameraNote={cue.cameraNote}
                        />
                      ) : null}
                    </>
                  )}
                </div>
              ) : null;

            const audioOnlyMeta =
              audioOnly && showMetaRow ? (
                <div className="sm-cue-meta-row">
                  {showSetupNumbers ? (
                    <span
                      className="sm-setup-num"
                      aria-label={`Indstilling ${sceneNumber}-${index + 1}`}
                    >
                      {sceneNumber}-{index + 1}
                    </span>
                  ) : null}
                  {showCameraNotes ? (
                    <CameraNoteLine
                      cueId={cue.id}
                      cameraNumber={cue.cameraNumber}
                      cameraNote={cue.cameraNote}
                      readOnly={readOnly}
                      onChange={metaChange}
                    />
                  ) : null}
                  {showPosition ? (
                    <PositionNoteLine
                      cueId={cue.id}
                      positionNote={cue.positionNote}
                      readOnly={readOnly}
                      onChange={metaChange}
                    />
                  ) : null}
                </div>
              ) : null;

            const visualMeta = audioOnly
              ? null
              : visualIsLeft
                ? col1MetaEl
                : col2MetaEl;
            const audioMeta = audioOnly
              ? audioOnlyMeta
              : visualIsLeft
                ? col2MetaEl
                : col1MetaEl;

            const cellRowChrome = readOnly
              ? {}
              : {
                  onInsertRow: () => handleInsertRowAfter(cue.id),
                  ...(index === 0
                    ? {
                        onInsertRowBefore: () =>
                          handleInsertRowBefore(cue.id),
                      }
                    : {}),
                };
            const visualInsertSide: "left" | "right" = visualIsLeft
              ? "left"
              : "right";
            const audioInsertSide: "left" | "right" =
              visualIsLeft && !audioOnly ? "right" : "left";

            const visualCell =
              visualSpan > 0 ? (
                <td
                  key="visual"
                  rowSpan={visualSpan}
                  className={`sm-cue-visual-cell${
                    visualSpan === 1 ? " sm-rule-edge" : ""
                  }${visualSpan > 1 ? " sm-is-rowspan" : ""}${
                    visualRuleEnd ? " sm-span-rule-end" : ""
                  }`}
                >
                  {showGripOnLeft && leftColumn === "visual" ? (
                    <button
                      type="button"
                      className="sm-row-grip"
                      aria-label="Flyt tekstpar"
                      title="Træk for at flytte tekstpar"
                      onPointerDown={(e) => beginRowDrag(cue.id, e)}
                    >
                      ⋮⋮
                    </button>
                  ) : null}
                  {showDeleteOnLeft && leftColumn === "visual" ? (
                    <button
                      type="button"
                      className="sm-row-delete sm-row-delete--in-visual"
                      disabled={cues.length <= 1}
                      aria-label="Slet række"
                      title="Slet række"
                      onClick={() => handleDeletePair(cue.id)}
                    >
                      ×
                    </button>
                  ) : null}
                  {showDeleteOnRight && rightColumn === "visual" ? (
                    <button
                      type="button"
                      className="sm-row-delete"
                      disabled={cues.length <= 1}
                      aria-label="Slet række"
                      title="Slet række"
                      onClick={() => handleDeletePair(cue.id)}
                    >
                      ×
                    </button>
                  ) : null}
                  {visualMeta}
                  <SideEditor
                    {...sideEditorProps}
                    {...cellRowChrome}
                    insertRowSide={visualInsertSide}
                    cue={cue}
                    column="visual"
                    selected={selectedKeys.has(cellKey(cue.id, "visual"))}
                    comments={commentsByCell(cue.id, "visual")}
                    requestFocus={
                      pendingFocus?.cueId === cue.id &&
                      pendingFocus.column === "visual"
                    }
                    focusSelectQuery={
                      pendingFocus?.cueId === cue.id &&
                      pendingFocus.column === "visual"
                        ? pendingFocus.selectQuery
                        : undefined
                    }
                    focusSelectOccurrence={
                      pendingFocus?.cueId === cue.id &&
                      pendingFocus.column === "visual"
                        ? pendingFocus.selectOccurrence
                        : undefined
                    }
                    focusSelectNonce={
                      pendingFocus?.cueId === cue.id &&
                      pendingFocus.column === "visual"
                        ? pendingFocus.selectNonce
                        : undefined
                    }
                    hideActiveChrome={
                      pendingFocus != null &&
                      (pendingFocus.cueId !== cue.id ||
                        pendingFocus.column !== "visual")
                    }
                    onFocused={() => {
                      setPendingFocus((cur) =>
                        cur?.cueId === cue.id && cur.column === "visual"
                          ? null
                          : cur,
                      );
                      onFocusCellHandled?.({ cueId: cue.id, column: "visual" });
                    }}
                  />
                  {!readOnly && spanEdit ? (
                    <div
                      className="sm-visual-resize"
                      title="Træk for at forlænge eller afkorte billedet"
                      role="separator"
                      aria-orientation="horizontal"
                      aria-label="Tilpas billedhøjde"
                      onPointerDown={(e) =>
                        beginVisualResize(cue.id, index, e)
                      }
                    >
                      {effectiveVisualSpan > 1 ? (
                        <span className="sm-span-badge">
                          {effectiveVisualSpan} rækker
                        </span>
                      ) : null}
                    </div>
                  ) : SHOW_ROW_SPAN_UI && visualSpan > 1 ? (
                    <span className="sm-span-badge sm-span-badge-static">
                      {visualSpan} rækker
                    </span>
                  ) : null}
                  {ruleOnVisual ? (
                    <RowRuleHit
                      extend={ruleOnAudio ? null : ruleExtend}
                      active={showSplitHit ? false : ruleBelow}
                      splitPick={showSplitHit}
                      onToggle={() => {
                        if (showSplitHit) return;
                        onTogglePrintBorderBelow(cue.id);
                      }}
                    />
                  ) : null}
                </td>
              ) : null;

            const audioCell =
              audioSpan > 0 ? (
                <td
                  key="audio"
                  rowSpan={audioSpan}
                  className={`sm-cue-audio-cell${
                    audioSpan === 1 ? " sm-rule-edge" : ""
                  }${audioSpan > 1 ? " sm-is-rowspan" : ""}${
                    audioRuleEnd ? " sm-span-rule-end" : ""
                  }`}
                >
                  {showGripOnLeft && leftColumn === "audio" ? (
                    <button
                      type="button"
                      className="sm-row-grip"
                      aria-label="Flyt tekstpar"
                      title="Træk for at flytte tekstpar"
                      onPointerDown={(e) => beginRowDrag(cue.id, e)}
                    >
                      ⋮⋮
                    </button>
                  ) : null}
                  {showDeleteOnLeft && leftColumn === "audio" ? (
                    <button
                      type="button"
                      className="sm-row-delete sm-row-delete--in-visual"
                      disabled={cues.length <= 1}
                      aria-label="Slet række"
                      title="Slet række"
                      onClick={() => handleDeletePair(cue.id)}
                    >
                      ×
                    </button>
                  ) : null}
                  {showDeleteOnRight && rightColumn === "audio" ? (
                    <button
                      type="button"
                      className="sm-row-delete"
                      disabled={cues.length <= 1}
                      aria-label="Slet række"
                      title="Slet række"
                      onClick={() => handleDeletePair(cue.id)}
                    >
                      ×
                    </button>
                  ) : null}
                  {audioMeta}
                  <SideEditor
                    {...sideEditorProps}
                    {...cellRowChrome}
                    insertRowSide={audioInsertSide}
                    cue={cue}
                    column="audio"
                    selected={selectedKeys.has(cellKey(cue.id, "audio"))}
                    comments={commentsByCell(cue.id, "audio")}
                    requestFocus={
                      pendingFocus?.cueId === cue.id &&
                      pendingFocus.column === "audio"
                    }
                    focusSelectQuery={
                      pendingFocus?.cueId === cue.id &&
                      pendingFocus.column === "audio"
                        ? pendingFocus.selectQuery
                        : undefined
                    }
                    focusSelectOccurrence={
                      pendingFocus?.cueId === cue.id &&
                      pendingFocus.column === "audio"
                        ? pendingFocus.selectOccurrence
                        : undefined
                    }
                    focusSelectNonce={
                      pendingFocus?.cueId === cue.id &&
                      pendingFocus.column === "audio"
                        ? pendingFocus.selectNonce
                        : undefined
                    }
                    hideActiveChrome={
                      pendingFocus != null &&
                      (pendingFocus.cueId !== cue.id ||
                        pendingFocus.column !== "audio")
                    }
                    onFocused={() => {
                      setPendingFocus((cur) =>
                        cur?.cueId === cue.id && cur.column === "audio"
                          ? null
                          : cur,
                      );
                      onFocusCellHandled?.({ cueId: cue.id, column: "audio" });
                    }}
                  />
                  {!readOnly && spanEdit ? (
                    <div
                      className="sm-audio-resize"
                      title="Træk for at forlænge eller afkorte lyden"
                      role="separator"
                      aria-orientation="horizontal"
                      aria-label="Tilpas lydhøjde"
                      onPointerDown={(e) =>
                        beginAudioResize(cue.id, index, e)
                      }
                    >
                      {effectiveAudioSpan > 1 ? (
                        <span className="sm-span-badge">
                          {effectiveAudioSpan} rækker
                        </span>
                      ) : null}
                    </div>
                  ) : SHOW_ROW_SPAN_UI && audioSpan > 1 ? (
                    <span className="sm-span-badge sm-span-badge-static">
                      {audioSpan} rækker
                    </span>
                  ) : null}
                  {ruleOnAudio ? (
                    <RowRuleHit
                      extend={ruleOnVisual ? null : ruleExtend}
                      active={showSplitHit ? false : ruleBelow}
                      splitPick={showSplitHit}
                      onToggle={() => {
                        if (showSplitHit) return;
                        onTogglePrintBorderBelow(cue.id);
                      }}
                    />
                  ) : null}
                </td>
              ) : null;

            return (
              <Fragment key={cue.id}>
                <tr
                  className={`sm-cue-row${ruleBelow ? " sm-rule-below" : ""}${
                    dragCueId === blockCueId ? " is-dragging" : ""
                  }${inResizeCover ? " is-span-cover-preview" : ""}${
                    visualSpan === 0 ? " sm-cue-visual-covered" : ""
                  }${audioSpan === 0 ? " sm-cue-audio-covered" : ""}${
                    splitHover?.index === index ? " is-split-hover" : ""
                  }${dropClass}`}
                  data-cue-id={cue.id}
                  data-cue-index={index}
                  data-block-cue-id={blockCueId}
                >
                  {audioOnly ? (
                    audioCell
                  ) : visualIsLeft ? (
                    <>
                      {visualCell}
                      {audioCell}
                    </>
                  ) : (
                    <>
                      {audioCell}
                      {visualCell}
                    </>
                  )}
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {splitPickMode && splitHover ? (
        <div
          className="sm-split-pick-line"
          aria-hidden
          style={{ top: splitHover.top }}
        />
      ) : null}
      {dragCueId && dropLineTop != null ? (
        <div
          className="sm-row-drop-line"
          aria-hidden
          style={{ top: dropLineTop }}
        />
      ) : null}
      {spanGhost ? (
        <div
          className="sm-span-resize-ghost"
          aria-hidden
          style={{
            top: spanGhost.top,
            left: spanGhost.left,
            width: spanGhost.width,
            height: spanGhost.height,
          }}
        />
      ) : null}
      </div>
      {spanConflict ? (
        <SpanConflictDialog
          side={spanConflict.side}
          previews={spanConflict.previews}
          onCancel={() => setSpanConflict(null)}
          onClear={() => {
            if (spanConflict.side === "visual") {
              onSetVisualSpan(spanConflict.cueId, spanConflict.span, "clear");
            } else {
              onSetAudioSpan(spanConflict.cueId, spanConflict.span, "clear");
            }
            setSpanConflict(null);
          }}
          onMerge={() => {
            if (spanConflict.side === "visual") {
              onSetVisualSpan(spanConflict.cueId, spanConflict.span, "merge");
            } else {
              onSetAudioSpan(spanConflict.cueId, spanConflict.span, "merge");
            }
            setSpanConflict(null);
          }}
        />
      ) : null}
    </div>
  );
}

function RowRuleHit({
  extend,
  active,
  splitPick,
  onToggle,
}: {
  extend?: "extend-left" | "extend-right" | null;
  active: boolean;
  splitPick?: boolean;
  onToggle: () => void;
}) {
  const extendClass =
    extend === "extend-left"
      ? " sm-row-rule-hit--extend-left"
      : extend === "extend-right"
        ? " sm-row-rule-hit--extend-right"
        : "";
  return (
    <button
      type="button"
      className={`sm-row-rule-hit${extendClass}${
        splitPick ? " sm-row-rule-hit--split" : ""
      }`}
      title={
        splitPick
          ? "Del scenen her — sidste række ovenfor, første række nedenfor"
          : active
            ? "Skjul vandret streg på udskrift"
            : "Vis vandret streg på udskrift"
      }
      aria-pressed={splitPick ? undefined : active}
      aria-label={
        splitPick
          ? "Opdel scenen ved denne linje"
          : "Skift vandret streg under denne række"
      }
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
    />
  );
}

function useStateFocus(initial: CellRef | null | undefined) {
  return useState<CellRef | null>(initial ?? null);
}
